/**
 * Block texture pixels.
 *
 * Painted in code, like the skins. Pure - no DOM, no engine - so the textures
 * can be rendered and inspected headlessly, and so the same pixels feed both
 * the GPU texture array and the inventory icon sheet.
 */

import { ATLAS_TILES, TILE_PX, TILE_COUNT } from "./tiles.js";

/** Deterministic per-pixel jitter so textures look grainy, not flat. */
function noise(x: number, y: number, salt: number): number {
  let h = x * 374761393 + y * 668265263 + salt * 1442695040;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4294967296;
}

export interface TileSpec {
  /** Base colour as [r, g, b], 0-255. */
  base: [number, number, number];
  /** How much per-pixel variation to apply, 0-1. */
  grain: number;
  /** Optional second colour speckled over the base. */
  /**
   * Scattered pixels of a second colour, and optionally of a second opacity.
   *
   * The alpha is there for water: the bright crests read as paint splashed on
   * top of a clear surface unless they are more see-through than the water
   * they sit in, not less.
   */
  fleck?: { color: [number, number, number]; chance: number; alpha?: number };
  /**
   * How opaque the drawn pattern is. Opaque by default.
   *
   * Only for a pattern that is the *same substance* as the surface under it,
   * which in practice means the streaks on running water. A window frame is
   * wood on glass and stays solid.
   */
  patternAlpha?: number;
  /**
   * Structure drawn over the grain. Worked stone and thatch read as noise
   * without it - the pattern is what says "somebody made this".
   */
  pattern?: "brick" | "cobble" | "courses" | "thatch" | "bark" | "rings" | "planks"
    | "wheat" | "grains" | "furrows" | "stick"
    | "hoe" | "hoeIron" | "crop" | "door" | "glass" | "window"
    | "stoveSide" | "stoveFront" | "stoveTop" | "shutter"
    | "bedPillow" | "bedCover" | "bedSide" | "bedEnd"
    | "vatSide" | "vatTop" | "bucket"
    | "flax" | "stalks" | "fibre" | "linen" | "wheel" | "rushnyk"
    | "hemp" | "current"
    | "pickaxe" | "shovel" | "sabre" | "ironSabre" | "woodenSabre" | "adamantSabre"
    | "axe" | "bow" | "arrow"
    | "crystal" | "flagUA" | "flagBlack"
    | "powder" | "rubber" | "shell" | "tank"
    | "ore" | "lump" | "chestFront" | "chestSide" | "chestTop" | "ingot" | "wallEdge"
    | "spawner"
    | "helm" | "mail" | "bracers" | "boots" | "slabIcon" | "stairIcon"
    | "tomato" | "sapling" | "grassSide" | "meat" | "karas" | "rod" | "ice"
    | "flint" | "boat" | "bone" | "pelt" | "wattle" | "wattleTop"
    | "cursed" | "cursedBrick" | "cursedLamp" | "whitewash" | "shield"
    | "needles" | "torch" | "resin" | "blueprint" | "blueprintTop"
    | "roofTile" | "gilt" | "star" | "creature"
    | "goldBit" | "toiletSide" | "toiletTop" | "orcSabre" | "orcSabreRude"
    | "trebuchetSide" | "trebuchetTop" | "trebuchetFront" | "firebolt"
    | "drone" | "battery" | "dynamite" | "remote"
    | "beetCrop" | "beet" | "beetSeeds";
  /** Whether a vessel tile is drawn holding water. */
  filled?: boolean;
  /** A whitewashed stove is painted; a bare clay one is not. */
  painted?: boolean;
  /** Whether a "stoveFront" tile has a fire in its mouth. */
  burning?: boolean;
  /** Whether a "drone" tile has any power left in it. */
  charged?: boolean;
  /**
   * Whether a "bark" tile runs its furrows across the tile instead of down.
   *
   * For a колода lying on its side: bark grows along the trunk, so a log laid
   * flat wants its furrows laid flat with it. Without this a horizontal log
   * is a vertical log's texture wrapped round a horizontal shape, which reads
   * as a mistake from any distance at all.
   */
  across?: boolean;
  /** Frame colour for a "window" tile. */
  frame?: [number, number, number];
  /** Which half of a door a "door" tile draws. */
  doorHalf?: "lower" | "upper";
  /** Door styling: heavy braced planks, or a framed panel. */
  doorStyle?: "braced" | "panelled";
  /** Whether a "meat" tile draws it raw or out of the fire. */
  cooked?: boolean;
  /** Whether a "meat" tile is poultry, which is paler both raw and cooked. */
  poultry?: boolean;
  /** Which tree a "sapling" tile draws. */
  species?: "oak" | "acacia" | "pine";
  /** Which face a "creature" tile draws. */
  beast?: "pig" | "tur" | "kurka" | "sobaka" | "kit"
    | "karas" | "orc" | "selianyn" | "selianka";
  /** Which growth stage a "crop" tile draws: 0 shoots, 1 green, 2 ripe. */
  cropStage?: 0 | 1 | 2;
  /**
   * Chance a pixel is cut out entirely, 0-1. Leaves need this: a solid green
   * cube reads as a hedge, and you should be able to see sky through a canopy.
   * Rendered with an alpha test, so no sorting is involved.
   */
  holes?: number;
  /** Constant opacity, 0-255. Only meaningful for blocks drawn blended. */
  alpha?: number;
  /** Colour of the pattern's mortar, grout or shadow lines. */
  line?: [number, number, number];
}

/**
 * Index matches the tile numbers in the block registry.
 *
 * The soil palette is the game's signature: green on top, near-black
 * underneath. Ukraine sits on the largest chornozem belt in the world, so a
 * cut bank of earth should read as black, not the ruddy brown Minecraft uses.
 */
export const TILES: Record<number, TileSpec> = {
  // 0 - unused (air)
  0: { base: [0, 0, 0], grain: 0 },
  // 1 - grass top: steppe green, a little yellowed by sun
  1: { base: [95, 140, 58], grain: 0.15, fleck: { color: [124, 168, 72], chance: 0.26 } },
  // 2 - dirt: dark earth, the layer just under the turf
  /*
   * 2 - земля.
   *
   * Taken off a photograph of a cut bank rather than chosen by eye: the middle
   * of that soil measures [138, 118, 98], a warm light brown, against the
   * [62, 47, 38] this used to be. It was the single biggest reason the world
   * came out dark - dirt and the sides of grass blocks together are more than
   * half of every land face you can see, and both sat at a luminance of 50.
   */
  2: { base: [138, 118, 98], grain: 0.20, fleck: { color: [162, 141, 116], chance: 0.18 } },
  /*
   * 3 - grass side: the same earth, with the turf showing along the top.
   *
   * There was no green on this face at all for a long time, deliberately - the
   * green stayed on top, which is the one thing every voxel game does the same
   * way. It reads as a block of soil with a lid, though, so the turf now comes
   * over the edge: a band across the top with single blades hanging into the
   * soil below it, rather than the solid ragged stripe.
   */
  3: {
    base: [138, 118, 98], grain: 0.20, fleck: { color: [162, 141, 116], chance: 0.18 },
    pattern: "grassSide", line: [84, 124, 50],
  },
  // 4 - stone
  4: { base: [126, 126, 130], grain: 0.14 },
  /*
   * 5 - chornozem: the black earth itself.
   *
   * Measured off a photograph of tilled чорнозем rather than guessed: the
   * middle of that soil is [81, 74, 68] - dark, but grey-brown rather than
   * warm, and nowhere near the near-black [34, 28, 26] this began as. The
   * grain is high because the photograph is all crumb and clod, and that
   * broken texture is most of what makes it read as чорнозем rather than as
   * a flat brown surface.
   *
   * Still the darkest soil in the game, and it should stay that way: the
   * lighter земля is the subsoil under it.
   */
  5: { base: [84, 76, 70], grain: 0.30, fleck: { color: [112, 102, 94], chance: 0.22 } },
  // 6 - sand: Dnipro riverbank
  6: { base: [214, 198, 148], grain: 0.10 },
  // 7 - clay (глина): the raw material for мазанка and цегла
  7: { base: [186, 142, 106], grain: 0.12 },
  // 8 - солома: dried grass, golden, laid in strands
  8: {
    base: [201, 168, 84], grain: 0.20, pattern: "thatch",
    line: [166, 133, 58], fleck: { color: [222, 193, 112], chance: 0.22 },
  },
  // 9 - цегла: fired clay laid in courses with pale mortar
  9: { base: [158, 76, 56], grain: 0.13, pattern: "brick", line: [206, 192, 172] },
  // 10 - мазанка: clay and straw daub under lime whitewash
  10: {
    base: [238, 233, 220], grain: 0.07,
    fleck: { color: [223, 214, 194], chance: 0.16 },
  },
  // 11 - пісковик: compacted sand, faintly bedded
  11: { base: [206, 186, 138], grain: 0.09, pattern: "courses", line: [180, 160, 116] },
  // 12 - бруківка: irregular cobbles with dark joints
  12: { base: [126, 126, 130], grain: 0.16, pattern: "cobble", line: [74, 74, 78] },
  // 13 - кам\'яна плитка: dressed stone, laid square
  13: { base: [146, 144, 141], grain: 0.08, pattern: "courses", line: [104, 102, 100] },
  // 14 - oak bark: dark, deeply furrowed
  14: { base: [92, 68, 44], grain: 0.14, pattern: "bark", line: [58, 42, 27] },
  // 15 - oak end grain: pale heartwood with rings
  15: { base: [166, 130, 88], grain: 0.10, pattern: "rings", line: [120, 90, 58] },
  // 16 - oak leaves: deep summer green, and you can see through it
  16: {
    base: [58, 96, 42], grain: 0.26, holes: 0.22,
    fleck: { color: [80, 122, 52], chance: 0.30 },
  },
  // 17 - acacia bark: paler and smoother than oak
  17: { base: [124, 108, 88], grain: 0.12, pattern: "bark", line: [88, 76, 60] },
  // 18 - acacia end grain
  18: { base: [196, 174, 134], grain: 0.09, pattern: "rings", line: [150, 128, 92] },
  // 19 - acacia leaves: lighter, yellower, and airier than oak
  19: {
    base: [104, 138, 62], grain: 0.24, holes: 0.30,
    fleck: { color: [136, 166, 80], chance: 0.32 },
  },
  // 20 - oak planks: warm and mid-toned
  20: { base: [168, 128, 80], grain: 0.11, pattern: "planks", line: [120, 88, 52] },
  // 21 - вода: blended, so alpha is the whole point.
  //
  // At 186 of 255 it was three-quarters opaque, and the surface seen from
  // below at a grazing angle became a ceiling of blue slabs with the lake bed
  // nowhere in it. Water you cannot see through is not water; this is close to
  // half, and the mottling is quieter, because strong per-pixel contrast on a
  // surface viewed almost edge-on is what turned it into banding.
  21: { base: [58, 108, 156], grain: 0.06, alpha: 140,
        // The crests are thinner than the water, not thicker. Physically it is
        // the wrong way round; on screen a bright fleck at the water's own
        // opacity reads as paint dropped on glass, and the lake bed disappears
        // under a rash of blue dots.
        fleck: { color: [86, 140, 190], chance: 0.12, alpha: 92 } },
  // 22 - дика пшениця: drawn as crossed quads, so most of it is empty
  22: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "wheat",
        line: [186, 158, 74] },
  // 23 - насіння пшениці: an item, never a block
  23: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "grains" },
  // 24 - рілля, суха: broken earth turned into ridges
  24: { base: [104, 78, 58], grain: 0.16, pattern: "furrows", line: [74, 54, 38] },
  // 25 - рілля, волога: the same soil, darker and richer for having water
  25: { base: [62, 46, 34], grain: 0.14, pattern: "furrows", line: [40, 29, 21] },
  // 26 - палиця
  26: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "stick", line: [138, 102, 60] },
  // 27 - дерев'яна сапка
  27: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "hoe", line: [138, 102, 60] },
  // 29-31 - the three stages of sown wheat. Height and colour both change, so
  // a field can be read at a glance from across the valley.
  28: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "crop", cropStage: 0 },
  29: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "crop", cropStage: 1 },
  30: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "crop", cropStage: 2 },
  // 31 - залізна сапка. Painted and reserved: iron is not smeltable yet, so
  // nothing in the game hands one out. Kept here so the wooden hoe has
  // something to be visibly worse than.
  31: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "hoeIron" },

  // 32-33 - дубові двері: heavy dark boards with iron straps across them.
  32: { base: [104, 74, 44], grain: 0.10, pattern: "door",
        doorHalf: "lower", doorStyle: "braced", line: [58, 42, 26] },
  33: { base: [104, 74, 44], grain: 0.10, pattern: "door",
        doorHalf: "upper", doorStyle: "braced", line: [58, 42, 26] },
  // 34-35 - двері з акації: paler, and framed rather than braced.
  34: { base: [166, 142, 106], grain: 0.09, pattern: "door",
        doorHalf: "lower", doorStyle: "panelled", line: [118, 98, 70] },
  35: { base: [166, 142, 106], grain: 0.09, pattern: "door",
        doorHalf: "upper", doorStyle: "panelled", line: [118, 98, 70] },
  // 36 - acacia planks: paler and greyer than oak, as the timber is
  36: { base: [198, 176, 138], grain: 0.09, pattern: "planks", line: [152, 132, 98] },

  // 37 - скло. Alpha well clear of the shader's 0.4 discard threshold, or
  // distant panes would start vanishing as mipmaps averaged them down.
  37: { base: [206, 226, 236], grain: 0.03, alpha: 122, pattern: "glass",
        line: [232, 244, 250] },
  // 38-39 - вікна: glass behind a wooden frame and a cross mullion, one per
  // wood, matching the doors.
  38: { base: [206, 226, 236], grain: 0.03, alpha: 122, pattern: "window",
        frame: [140, 100, 58], line: [232, 244, 250] },
  39: { base: [206, 226, 236], grain: 0.03, alpha: 122, pattern: "window",
        frame: [190, 166, 126], line: [232, 244, 250] },

  // 40-42 - піч of bare clay and brick, warm and unfinished.
  40: { base: [186, 142, 104], grain: 0.13, pattern: "stoveSide",
        line: [140, 104, 74] },
  41: { base: [186, 142, 104], grain: 0.13, pattern: "stoveFront",
        line: [140, 104, 74] },
  42: { base: [172, 130, 96], grain: 0.11, pattern: "stoveTop",
        line: [132, 98, 70] },
  // 43-45 - the same піч limed white and painted, like the walls of the хата.
  43: { base: [238, 233, 220], grain: 0.05, pattern: "stoveSide",
        line: [214, 206, 190], painted: true },
  44: { base: [238, 233, 220], grain: 0.05, pattern: "stoveFront",
        line: [214, 206, 190], painted: true },
  // The same two stove fronts with a fire in the mouth.
  120: { base: [186, 142, 104], grain: 0.13, pattern: "stoveFront",
        line: [140, 104, 74], burning: true },
  121: { base: [238, 233, 220], grain: 0.05, pattern: "stoveFront",
        line: [214, 206, 190], painted: true, burning: true },
  // The креслярський камінь: dressed grey stone with a plan cut into it.
  122: { base: [120, 124, 132], grain: 0.10, pattern: "blueprint", line: [64, 88, 118] },
  123: { base: [132, 136, 144], grain: 0.09, pattern: "blueprintTop", line: [64, 88, 118] },
  45: { base: [232, 227, 214], grain: 0.05, pattern: "stoveTop",
        line: [206, 198, 182], painted: true },

  // 46-47 - віконниці: ledged boards with a carved heart, the way a хата's
  // shutters are cut. One per wood, matching the doors and windows.
  46: { base: [150, 108, 62], grain: 0.10, pattern: "shutter", line: [98, 68, 38] },
  47: { base: [196, 172, 132], grain: 0.09, pattern: "shutter", line: [146, 124, 90] },

  // 48-51 - ліжко. The frame is plain timber; the character is the textile.
  // Cross-stitch geometry in red and black on unbleached linen - ромби and
  // zigzags, the pattern off any вишиванка or woven ряднина.
  48: { base: [238, 232, 216], grain: 0.04, pattern: "bedPillow" },
  49: { base: [232, 224, 204], grain: 0.05, pattern: "bedCover" },
  50: { base: [232, 224, 204], grain: 0.05, pattern: "bedSide",
        frame: [140, 100, 58] },
  51: { base: [140, 100, 58], grain: 0.10, pattern: "bedEnd", line: [96, 68, 38] },

  // 52-54 - глиняний чан: fired clay, hooped, for retting flax.
  52: { base: [172, 118, 84], grain: 0.12, pattern: "vatSide", line: [124, 82, 56] },
  53: { base: [172, 118, 84], grain: 0.12, pattern: "vatTop", line: [124, 82, 56] },
  54: { base: [172, 118, 84], grain: 0.12, pattern: "vatTop", line: [124, 82, 56],
        filled: true },
  // 55-56 - глиняне відро, empty and full.
  55: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "bucket",
        line: [176, 122, 86] },
  56: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "bucket",
        line: [176, 122, 86], filled: true },

  // 57 - дикий льон, and 58-60 the sown crop. Flax is told from wheat at a
  // glance by its flowers: a field of it in bloom is pale blue.
  57: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flax", cropStage: 2 },
  58: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flax", cropStage: 0 },
  59: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flax", cropStage: 1 },
  60: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flax", cropStage: 2 },
  // 61-63 - what comes out of it: stalks, retted fibre, woven linen.
  61: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "stalks",
        line: [150, 146, 108] },
  62: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "fibre",
        line: [214, 208, 178] },
  63: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "linen" },
  // 64 - прядка, 65 - рушник.
  64: { base: [156, 116, 70], grain: 0.11, pattern: "wheel", line: [108, 78, 46] },
  65: { base: [240, 235, 222], grain: 0.04, pattern: "rushnyk" },
  // 66 - дикі коноплі. Read at a glance by the leaf, not by the stalk.
  66: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "hemp" },
  // 96 - оркське лігво: dark rock with the letter cut into it, so it is
  // unmistakable from across the island and obviously the thing to break.
  96: { base: [46, 42, 44], grain: 0.16, pattern: "spawner", line: [150, 38, 34] },
  // 95 - the half-thickness мазанка wall. The same limewash as the full block
  // with a shadowed edge, so a thin wall reads as thin from across the room
  // rather than as a full one that has lost half of itself.
  95: {
    base: [238, 233, 220], grain: 0.07,
    fleck: { color: [223, 214, 194], chance: 0.16 },
    pattern: "wallEdge", line: [196, 188, 172],
  },
  // 93-94 - what comes out of the fire. Bars rather than lumps, so a smelted
  // thing is told from the rock it came out of at a glance in the hotbar.
  93: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "ingot", line: [188, 190, 198] },
  94: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "ingot", line: [162, 148, 178] },
  // 168 - the same bar, cast in gold. Out of a buried chest and nowhere else.
  168: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "ingot", line: [214, 172, 58] },
  /*
   * 169-173 - луска дракона and the four things made of it.
   *
   * The same four shapes the iron kit uses, in the Змій's own green with a
   * gold edge, so a player can tell across a room which set somebody is
   * wearing. The scale itself is the ore-lump shape: a thing you pick up off
   * the ground, not a thing that came out of a fire.
   */
  169: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [63, 107, 52] },
  170: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "helm", line: [63, 107, 52] },
  171: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "mail", line: [63, 107, 52] },
  172: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "bracers", line: [63, 107, 52] },
  173: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "boots", line: [63, 107, 52] },
  /*
   * 174 - the half-thickness granite wall.
   *
   * The same treatment tile 95 gives the мазанка one: the block's own stone
   * with a shadowed edge, so a thin wall reads as thin from across the room
   * rather than as a full one that has lost half of itself.
   */
  174: {
    base: [148, 132, 126], grain: 0.11,
    fleck: { color: [118, 104, 100], chance: 0.18 },
    pattern: "wallEdge", line: [96, 86, 82],
  },
  /*
   * 175-176 - the two плити, as icons.
   *
   * The block wears its parent's own tile - half a block of мазанка is
   * мазанка - but the thing in the hotbar has to say what it puts down, and
   * a plain square of daub says "a block of daub". The `slabIcon` pattern is
   * the material filling the bottom half of the square and nothing in the
   * top, which is the shape you get.
   */
  /*
   * 200-201 - the two сходи, as icons.
   *
   * The same thought as the плити above: the block wears its material's
   * tile, and in the hotbar that is a square of stone that says "a block
   * of stone" - "поміняй їх картинку, щоб було видно, що то сходи". The
   * `stairIcon` pattern is the material as a step seen from the side: the
   * bottom half full width, the top half only on the right, and a darker
   * line along every edge that faces up or forward.
   */
  200: { base: [126, 126, 130], grain: 0.14, alpha: 0,
         pattern: "stairIcon", line: [88, 88, 92] },
  201: { base: [168, 128, 80], grain: 0.11, alpha: 0,
         pattern: "stairIcon", line: [120, 88, 52] },
  175: {
    base: [238, 233, 220], grain: 0.07, alpha: 0,
    fleck: { color: [223, 214, 194], chance: 0.16 },
    pattern: "slabIcon", line: [196, 188, 172],
  },
  176: {
    base: [166, 128, 120], grain: 0.13, alpha: 0,
    fleck: { color: [206, 176, 168], chance: 0.30 },
    pattern: "slabIcon", line: [110, 84, 78],
  },
  // 90-92 - the скриня: painted front, plain banded sides, banded lid. A
  // dowry chest was the one piece of furniture a house painted, so the front
  // gets a flower and the rest gets ironwork.
  90: { base: [122, 82, 46], grain: 0.12, pattern: "chestFront",
        line: [64, 56, 52] },
  91: { base: [122, 82, 46], grain: 0.12, pattern: "chestSide",
        line: [64, 56, 52] },
  92: { base: [134, 92, 52], grain: 0.12, pattern: "chestTop",
        line: [64, 56, 52] },
  // 73-81 - what is under the ground. Ore tiles are the host rock with the
  // mineral showing through, so a seam reads as "stone with something in it"
  // rather than as a solid block of coal.
  73: { base: [88, 88, 92], grain: 0.10, pattern: "ore", line: [28, 28, 32] },
  74: { base: [88, 88, 92], grain: 0.10, pattern: "ore", line: [172, 110, 74] },
  75: { base: [88, 88, 92], grain: 0.10, pattern: "ore", line: [126, 106, 148] },
  76: { base: [88, 88, 92], grain: 0.10, pattern: "ore", line: [64, 66, 74] },
  77: { base: [74, 70, 66], grain: 0.14, pattern: "ore", line: [34, 28, 24] },
  // Granite is not ore: speckled pink feldspar and grey quartz, no seam.
  78: { base: [166, 128, 120], grain: 0.13,
        fleck: { color: [206, 176, 168], chance: 0.30 } },
  // Peat: dark, fibrous, and the one mineral here you dig with a spade.
  79: { base: [64, 48, 34], grain: 0.22,
        fleck: { color: [92, 72, 48], chance: 0.26 } },
  // Titanium comes out of sand, not rock - the placer deposits at Irshansk.
  // So the host here is sand, and that is the whole tell.
  80: { base: [206, 184, 130], grain: 0.13, pattern: "ore", line: [58, 52, 56] },
  81: { base: [206, 184, 130], grain: 0.13, pattern: "ore", line: [148, 96, 62] },
  // 82-89 - what you carry away: lumps of each, and peat cut into a brick.
  82: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [38, 38, 42] },
  83: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [172, 112, 76] },
  84: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [130, 110, 152] },
  85: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [74, 76, 84] },
  86: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [26, 22, 20] },
  87: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [72, 54, 38] },
  88: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [66, 60, 64] },
  89: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "lump", line: [154, 100, 66] },
  // 68-72 - the tools. All wooden: iron is not smeltable yet, and the шабля
  // is a training one, which is how they were actually made for learning.
  68: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "pickaxe" },
  // Сокира, in wood and in steel: one drawing, two metals, the way the two
  // picks and the two шаблі are each one drawing.
  186: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "axe" },
  /*
   * 188 - незламний блок: nearly black, with a hard flecked grain.
   *
   * It has to read as "do not bother" from across a room. Dark enough to be
   * plainly not stone, and speckled rather than flat so it does not look
   * like a hole in the world where the light has failed.
   */
  188: {
    base: [38, 38, 44], grain: 0.16,
    fleck: { color: [22, 22, 28], chance: 0.3 },
    pattern: "cobble", line: [24, 24, 30],
  },
  // 189 - адамантій in the rock: the stone of the deep, with blue-white
  // crystal growing through it.
  189: {
    base: [104, 104, 112], grain: 0.10,
    fleck: { color: [86, 86, 94], chance: 0.16 },
    pattern: "crystal", line: [168, 222, 240],
  },
  // 190, 191 - the адамантій kit: the same two drawings as iron and wood,
  // in the crystal's own colour.
  190: {
    base: [0, 0, 0], grain: 0, alpha: 0, pattern: "pickaxe",
    line: [168, 222, 240],
  },
  // A model in the hand now, like the other two sabres, and the picture is
  // that model - "тепер те саме з адамантовою шаблею".
  191: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "adamantSabre" },
  // 192, 193 - the прапори: everybody's, and the bots'.
  /*
   * 194 - нафта: nearly black, with the sheen that says it is not a hole.
   *
   * A flat black tile reads as a pit rather than as a liquid, so the grain
   * is high and the fleck is a dull green-gold - the colour an oil slick
   * throws back, and enough of it that the surface moves when you do.
   */
  194: {
    base: [22, 20, 18], grain: 0.35,
    fleck: { color: [58, 62, 38], chance: 0.1 },
  },
  // 195 - порох: coarse grey-black meal in a heap.
  195: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "powder" },
  // 196 - гума: a black block of it, dull and slightly uneven.
  196: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "rubber" },
  // 197 - снаряд: a brass case with a pointed steel head.
  197: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "shell" },
  // 198 - танк, seen from the side: tracks, hull, turret, gun.
  198: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "tank" },
  /*
   * 199 - шопопало-блок: dark brown, and something under the surface.
   *
   * Deliberately almost plain. The white lightning that makes it magic runs
   * over it in the terrain shader, moving and dying away; painted into the
   * tile it would be a permanent white scratch on a brown block, which is
   * what a still spark looks like.
   */
  199: {
    base: [78, 52, 34], grain: 0.2,
    fleck: { color: [112, 74, 48], chance: 0.3 },
  },
  192: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flagUA" },
  193: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flagBlack" },
  187: {
    base: [0, 0, 0], grain: 0, alpha: 0, pattern: "axe", line: [150, 152, 162],
  },
  // The iron kit. `line` is the metal, and the same colour runs through all
  // five so a full set reads as one suit rather than five finds.
  97: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "pickaxe", line: [150, 152, 162] },
  // The iron sabre is a model in the hand, so its picture is that model
  // shrunk to a tile - "онови картинку шаблі в інвентарі" - rather than the
  // wooden drawing recoloured.
  112: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "ironSabre" },
  // Сосна: plated red-brown bark, pale resinous heartwood, dark needles.
  113: { base: [138, 92, 62], grain: 0.14, pattern: "bark", line: [92, 58, 38] },
  114: { base: [214, 176, 126], grain: 0.09, pattern: "rings", line: [166, 126, 84] },
  115: { base: [46, 84, 60], grain: 0.10, pattern: "needles", holes: 0 },
  116: { base: [198, 156, 108], grain: 0.11, pattern: "planks", line: [148, 108, 68] },
  117: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "sapling", species: "pine" },
  118: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "torch" },
  119: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "resin" },
  98: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "helm" },
  99: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "mail" },
  100: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "bracers" },
  101: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "boots" },
  // Not an item: сила is drawn with these, one помідор to every two points.
  102: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "tomato" },
  103: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "sapling", species: "oak" },
  104: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "sapling", species: "acacia" },
  // Food. Raw and cooked are the same cut in two palettes.
  105: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "meat" },
  106: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "meat", cooked: true },
  107: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "meat" },
  108: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "meat", cooked: true },
  109: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "karas" },
  110: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "karas", cooked: true },
  111: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "rod" },
  69: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "shovel" },
  // A model in the hand now, like the iron one, so the picture is that
  // model shrunk - "зроби те саме, що для залізної".
  70: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "woodenSabre" },
  71: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "bow" },
  72: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "arrow" },
  // 67 - running water. Its own tile purely so the shader can find it: the
  // fragment shader scrolls this layer and leaves the still sea alone.
  67: { base: [46, 92, 168], grain: 0.08, alpha: 140, patternAlpha: 118,
        pattern: "current",
        line: [96, 150, 220] },

  /*
   * 124-129 - what a fortress and its cathedrals are made of.
   *
   * Белокаменный limestone, glazed roof tile, gilding and a spire star. The
   * red of the walls is the цегла this game already has: fired clay is fired
   * clay, and a second almost-identical red brick would be two blocks nobody
   * could tell apart in a хотбар.
   */
  124: { base: [222, 218, 206], grain: 0.055, pattern: "courses",
         line: [176, 170, 156] },
  125: { base: [46, 104, 68], grain: 0.08, pattern: "roofTile",
         line: [26, 66, 44] },
  126: { base: [206, 162, 62], grain: 0.07, pattern: "gilt",
         line: [150, 106, 30] },
  127: { base: [124, 22, 28], grain: 0.06, pattern: "star",
         line: [232, 74, 68] },
  // The кремль stone: the same dressed block as the хата one, with a
  // different plan cut into it and gilded rather than inked, so the two are
  // never confused in a row of grey squares.
  128: { base: [126, 118, 112], grain: 0.10, pattern: "blueprint",
         line: [178, 132, 44] },
  129: { base: [138, 130, 122], grain: 0.09, pattern: "blueprintTop",
         line: [178, 132, 44] },

  /*
   * 130-135 - a face each, for letting a creature out of the комора.
   *
   * Faces rather than whole animals: sixteen pixels will hold a snout, two
   * eyes and a pair of ears, and it will not hold a pig. Every version that
   * tried to draw the whole beast came out a brown smudge, and the player
   * picks these out of a shelf of a hundred squares.
   */
  130: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "pig" },
  131: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "tur" },
  132: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "karas" },
  133: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "orc" },
  134: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "selianyn" },
  135: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "selianka" },

  // 136-139 - what an orc leaves behind, and what its leader carries.
  136: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "goldBit" },
  /*
   * Transparent behind the shape.
   *
   * A унітаз is not a cube, and painted onto a full tile it was a white block
   * with a smudge on it. With the ground showing round the silhouette it
   * reads as a thing standing there - which is why the block is declared
   * non-opaque as well: it must not cull whatever is behind it.
   */
  137: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "toiletSide" },
  138: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "toiletTop" },
  139: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "orcSabre" },
  /*
   * 140 - the same weapon, as it looks with the 18+ switch on.
   *
   * A second tile rather than a second item: it is one sabre with one id, one
   * name and one damage, and only its picture changes. Which tile is drawn is
   * decided at draw time from the tone, the same way the lines are chosen -
   * and the clean one is the default, so forgetting to pass the tone shows
   * the polite picture rather than the other one.
   */
  140: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "orcSabreRude" },

  // 141-144 - the engine, and what it throws.
  141: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "trebuchetSide" },
  142: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "trebuchetTop" },
  143: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "trebuchetFront" },
  144: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "firebolt" },

  /*
   * 145-147 - the drone, the same drone flat, and the cell.
   *
   * Moved up from 141 when fire arrived and took those numbers: these were
   * written against a twelve-wide sheet with three slots left in it, and the
   * flames were already at 141 in the copy this came from. Transparent
   * behind, like everything else you hold - the shape is the whole picture.
   */
  145: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "drone", charged: true },
  146: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "drone", charged: false },
  147: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "battery" },
  /*
   * 177 - пульт, which lives at the end of the sheet rather than beside the
   * drone: 148 onwards were taken years of commits ago, and a tile number is
   * written into every save that ever mentioned a block.
   */
  177: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "remote" },
  /*
   * 178-180 - the same three barks, lying down.
   *
   * "Зроби щоб колоди можливо було ставити горизонтально." Bark grows along
   * the trunk: a log on its side with the standing tile on it has furrows
   * running the wrong way, and at any distance that reads as a mistake rather
   * than as a log. Same colours, same grain, furrows turned a quarter.
   */
  178: { base: [92, 68, 44], grain: 0.14, pattern: "bark", line: [58, 42, 27], across: true },
  179: { base: [124, 108, 88], grain: 0.12, pattern: "bark", line: [88, 76, 60], across: true },
  180: { base: [138, 92, 62], grain: 0.14, pattern: "bark", line: [92, 58, 38], across: true },
  /*
   * 181-185 - буряк: three stages in the ground, then the root and the seed.
   *
   * "Додай буряк і нехай в них росте теж." It grows in the городи beside the
   * wheat, so the two have to be told apart from across the village: wheat is
   * thin, vertical and goes gold, this is squat and dark with red at the foot
   * of it when it is ready.
   */
  181: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "beetCrop", cropStage: 0 },
  182: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "beetCrop", cropStage: 1 },
  183: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "beetCrop", cropStage: 2 },
  184: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "beet" },
  185: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "beetSeeds" },
  /*
   * 148 - вибухоблок, and every one of them identical.
   *
   * Low grain on purpose. Every other block in the game is freckled so that a
   * wall does not read as wallpaper, but here a wall *must* read as
   * wallpaper: the moment two blocks can be told apart, somebody starts
   * looking for the tell, and the map is about not being able to know.
   */
  148: {
    base: [52, 92, 168], grain: 0.03, pattern: "dynamite", line: [176, 52, 46],
  },
  /*
   * 149 - сніг. Nearly white, and freckled rather than flat.
   *
   * A perfectly even white is the one surface where the eye cannot tell
   * where the ground is: a snowfield with no grain reads as fog, and you
   * cannot see the shape of a hill you are standing on. The flecks are
   * brighter than the base rather than darker, which is what sunlight on
   * snow actually does.
   */
  149: {
    base: [226, 232, 240], grain: 0.05,
    fleck: { color: [248, 250, 255], chance: 0.22 },
  },
  /*
   * 150 - лід.
   *
   * It borrowed the water's own streaks and wore them still, on a blue only
   * a little lighter than the water's - which is a fine idea and completely
   * unreadable in the game: a frozen lake looked like a lake, and the first
   * person to walk onto one dug a block out of it and reported that the
   * water was not flowing.
   *
   * So: nearly white, and cracked rather than streaked. What tells ice from
   * water at a glance is that it is pale and that it is *broken* - straight
   * hard lines going somewhere, where water has soft ones going nowhere.
   */
  150: {
    base: [214, 234, 244], grain: 0.05, pattern: "ice",
    line: [126, 172, 204],
  },
  // Курятина: the same cut as the pork and the beef, in the paler palette
  // that poultry actually is.
  151: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "meat", poultry: true },
  152: {
    base: [0, 0, 0], grain: 0, alpha: 0,
    pattern: "meat", poultry: true, cooked: true,
  },
  153: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "kurka" },
  154: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "flint" },
  156: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "sobaka" },
  157: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "creature", beast: "kit" },
  158: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "boat" },
  159: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "bone" },
  160: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "pelt" },
  161: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "wattle" },
  162: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "wattleTop" },
  163: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "cursed" },
  164: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "cursedBrick" },
  165: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "cursedLamp" },
  166: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "whitewash" },
  167: { base: [0, 0, 0], grain: 0, alpha: 0, pattern: "shield" },
  /*
   * 155 - лава. The one thing in this game that is its own light.
   *
   * Bright orange with a dark crust drifting on it, which is what molten
   * rock looks like from above: the crust is cooler than what is under it,
   * so the dark is the pattern and the glow is the ground.
   */
  155: {
    base: [232, 116, 30], grain: 0.10,
    fleck: { color: [120, 44, 18], chance: 0.20 },
  },
};

/** The three threads of a вишиванка: madder red, black, and the linen under. */
const STITCH_RED: [number, number, number] = [186, 46, 42];
const STITCH_BLACK: [number, number, number] = [42, 34, 32];

/**
 * One band of cross-stitch: a row of diamonds with the zigzag between them.
 *
 * Wraps at the tile edge, because a bed is two blocks long and the pattern has
 * to run across the join without a seam.
 */
function stitchBand(
  draw: (x: number, y: number, r: number, g: number, b: number) => void,
  cy: number,
  primary: [number, number, number],
  secondary: [number, number, number],
): void {
  const R = 2;          // a five-by-five diamond
  const PERIOD = 8;     // divides 16, so the band meets across the tile edge

  for (let x = 0; x < TILE_PX; x++) {
    // Signed distance to the nearest diamond centre, wrapped.
    const phase = ((x - 3) % PERIOD + PERIOD) % PERIOD;
    const dx = phase <= PERIOD / 2 ? phase : phase - PERIOD;

    // The half-height of the diamond at this column. Taking it from the
    // horizontal distance is what makes it a diamond rather than the solid
    // bar the first attempt drew.
    const half = R - Math.abs(dx);
    if (half >= 0) {
      for (let dy = -half; dy <= half; dy++) {
        draw(x, cy + dy, primary[0], primary[1], primary[2]);
      }
    } else if (Math.abs(dx) === PERIOD / 2) {
      // A small upright mark halfway between the diamonds.
      for (let dy = -1; dy <= 1; dy++) {
        draw(x, cy + dy, secondary[0], secondary[1], secondary[2]);
      }
    }
  }
}

/** Wooden shaft shared by every tool: two pixels thick, lit along the top. */
/** Same colour, lighter or darker. Keeps a metal's three tones in step. */
function shade(
  c: [number, number, number], by: number,
): [number, number, number] {
  return [
    Math.max(0, Math.min(255, Math.round(c[0] * by))),
    Math.max(0, Math.min(255, Math.round(c[1] * by))),
    Math.max(0, Math.min(255, Math.round(c[2] * by))),
  ];
}

function drawShaft(
  draw: (x: number, y: number, r: number, g: number, b: number) => void,
  /** The row the shaft ends on, where the head takes over. */
  foot = 9,
): void {
  const wood: [number, number, number] = [140, 98, 54];
  const woodLit: [number, number, number] = [172, 122, 68];
  const woodDark: [number, number, number] = [98, 68, 38];
  /*
   * Straight up, one pixel wide, from the top of the tile to the head.
   *
   * It ran corner to corner as a staircase of pixels, which reads as a
   * diagonal at 16 px and as a bent stick once it is extruded and held at
   * arm's length: "держак вигнутий, а має бути прямим". A shaft is a
   * straight thing; the drawing's angle in the hand is the hand's business.
   * Two pixels wide it was a plank in the fist at arm's length; one is a
   * handle - "зроби рукоятку на 50% вужчою, металеву частину не чіпай".
   */
  // From the very edge of the tile: a fifth longer than it was - "зроби
  // держак довшим на 20%" - and the pick's head sits a row lower for it.
  void woodLit;
  for (let y = 0; y <= foot; y++) draw(8, y, wood[0], wood[1], wood[2]);
  for (let y = 2; y <= foot; y += 3) draw(8, y, woodDark[0], woodDark[1], woodDark[2]);
}

/**
 * Draw a tile's structural lines over the grain.
 *
 * All of these wrap at the tile edge, because a merged greedy quad repeats the
 * tile many times across one surface - a pattern that does not tile leaves a
 * visible grid across every wall.
 */
function drawPattern(out: Uint8Array, tile: number, spec: TileSpec): void {
  const line = spec.line ?? [0, 0, 0];
  const set = (x: number, y: number, mix: number): void => {
    if (x < 0 || y < 0 || x >= TILE_PX || y >= TILE_PX) return;
    const i = (y * TILE_PX + x) * 4;
    out[i] = out[i]! * (1 - mix) + line[0] * mix;
    out[i + 1] = out[i + 1]! * (1 - mix) + line[1] * mix;
    out[i + 2] = out[i + 2]! * (1 - mix) + line[2] * mix;
  };

  /**
   * Paint a pixel of a drawn shape.
   *
   * Opaque unless the tile says otherwise, which is right nearly everywhere: a
   * plant is an empty tile plus a shape, and the wooden frame of a window is
   * wood however clear the glass behind it is.
   *
   * The exception is a pattern drawn in the same substance as the surface it
   * is on - the streaks on flowing water - and that cannot be worked out from
   * the alpha, only stated. Inferring it from "the tile is already
   * see-through" was tried and it dissolved the window frames.
   */
  const shapeAlpha = spec.patternAlpha ?? 255;
  const draw = (x: number, y: number, r: number, g: number, b: number): void => {
    if (x < 0 || y < 0 || x >= TILE_PX || y >= TILE_PX) return;
    const i = (y * TILE_PX + x) * 4;
    out[i] = r;
    out[i + 1] = g;
    out[i + 2] = b;
    out[i + 3] = shapeAlpha;
  };

  switch (spec.pattern) {
    case "brick": {
      // Four courses of brick; every other course offset by half a brick.
      const courseHeight = 4;
      for (let y = 0; y < TILE_PX; y++) {
        if (y % courseHeight === 0) {
          for (let x = 0; x < TILE_PX; x++) set(x, y, 1);
          continue;
        }
        const course = Math.floor(y / courseHeight);
        const offset = course % 2 === 0 ? 0 : 4;
        for (let x = 0; x < TILE_PX; x++) {
          if ((x + offset) % 8 === 0) set(x, y, 1);
        }
      }
      break;
    }
    case "courses": {
      // Dressed stone: a horizontal joint every 8 rows, verticals staggered.
      for (let y = 0; y < TILE_PX; y++) {
        if (y % 8 === 0) {
          for (let x = 0; x < TILE_PX; x++) set(x, y, 0.85);
          continue;
        }
        const offset = Math.floor(y / 8) % 2 === 0 ? 0 : 8;
        for (let x = 0; x < TILE_PX; x++) {
          if ((x + offset) % 16 === 0) set(x, y, 0.85);
        }
      }
      break;
    }
    case "trebuchetSide":
    case "trebuchetTop":
    case "trebuchetFront": {
      /*
       * A timber frame with a throwing arm and a counterweight.
       *
       * Drawn on a transparent tile rather than over a full square: a siege
       * engine is a frame with daylight through it, and painted onto a solid
       * cube it read as a crate. The block is declared non-opaque for the
       * same reason - it must not cull what is behind it.
       */
      const side = [
        "..............ww",
        ".............wwe",
        "............ww..",
        "...........ww...",
        "..........ww....",
        ".........ww.....",
        "........ww......",
        ".......ww.......",
        "......ww........",
        ".....ww...bb....",
        "..w.ww....bb....",
        "..www...........",
        ".w..w...........",
        "ww...ww.........",
        "w.....ww........",
        "wwwwwwwwwwwwwwww",
      ];
      const top = [
        "wwwwwwwwwwwwwwww",
        "w..............w",
        "w..wwwwwwwwww..w",
        "w..w........w..w",
        "w..w..bbbb..w..w",
        "w..w..bbbb..w..w",
        "w..w........w..w",
        "w..w........w..w",
        "w..w........w..w",
        "w..w..eeee..w..w",
        "w..w..eeee..w..w",
        "w..w........w..w",
        "w..wwwwwwwwww..w",
        "w..............w",
        "w..............w",
        "wwwwwwwwwwwwwwww",
      ];
      const front = [
        "..ww........ww..",
        "..ww........ww..",
        "..ww........ww..",
        "...ww......ww...",
        "...ww......ww...",
        "....ww....ww....",
        "....wwwwwwww....",
        "....w......w....",
        "....w.bbbb.w....",
        "....w.bbbb.w....",
        "....w.bbbb.w....",
        "...ww......ww...",
        "...ww......ww...",
        "..ww........ww..",
        "..ww........ww..",
        "wwww........wwww",
      ];
      const colours: Record<string, [number, number, number]> = {
        w: [122, 88, 56], e: [92, 66, 42], b: [104, 100, 96],
      };
      const rows = spec.pattern === "trebuchetTop" ? top
        : spec.pattern === "trebuchetFront" ? front : side;
      for (let y = 0; y < TILE_PX; y++) {
        const row = rows[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "firebolt": {
      // A stone bound in cloth, already alight: the rag is what carries the
      // fire, and it is the part that has to read at this size.
      const MASK = [
        "................",
        "......ff........",
        ".....ffff.f.....",
        "....ff.fffff....",
        "...fcccccccf....",
        "..fcclllllccf...",
        "..fcllsssllcf...",
        "..fclsssssscf...",
        "..fclsssssslc...",
        "...fcllsssllc...",
        "....fccllllcc...",
        ".....fcccccf....",
        "......fffff.....",
        "................",
        "................",
        "................",
      ];
      const colours: Record<string, [number, number, number]> = {
        f: [244, 148, 44], c: [216, 200, 176], l: [176, 160, 140],
        s: [110, 108, 104],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "goldBit": {
      // A nugget, not a coin: lumpy, and lit from the top left like every
      // other small thing in this atlas.
      const MASK = [
        "................",
        "................",
        "................",
        "................",
        ".....hhhh.......",
        "....hllllh......",
        "...hlllllgh.....",
        "...hllllgggh....",
        "...hlllggggh....",
        "....hggggggh....",
        ".....hggggh.....",
        "......hhhh......",
        "................",
        "................",
        "................",
        "................",
      ];
      const colours: Record<string, [number, number, number]> = {
        l: [252, 228, 140], g: [214, 168, 62], h: [150, 110, 32],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "toiletSide":
    case "toiletTop": {
      /*
       * Porcelain, drawn rather than patterned.
       *
       * White noise on a white block is a white block: without the shape of a
       * cistern and a bowl this was a pale cube nobody could name. The top is
       * the seat seen from above, which is what an inventory icon shows.
       */
      const side = [
        "................",
        "...ccccccccc....",
        "...ccccccccc....",
        "...ccccccccc....",
        "...cccdddccc....",
        "...ccccccccc....",
        "..sssssssssss...",
        "..soooooooooss..",
        "..ssssssssssss..",
        "...bbbbbbbbbb...",
        "...bbbbbbbbbb...",
        "....bbbbbbbb....",
        ".....bbbbbb.....",
        ".....bbbbbb.....",
        "....bbbbbbbb....",
        "...bbbbbbbbbb...",
      ];
      const top = [
        "................",
        "................",
        "....ssssssss....",
        "...ssssssssss...",
        "..sss oooo sss..",
        "..ss oooooo ss..",
        "..s oooooooo s..",
        "..s oooooooo s..",
        "..s oooooooo s..",
        "..s oooooooo s..",
        "..ss oooooo ss..",
        "..sss oooo sss..",
        "...ssssssssss...",
        "....ssssssss....",
        "................",
        "................",
      ];
      const colours: Record<string, [number, number, number]> = {
        c: [246, 246, 242], s: [232, 232, 228], b: [238, 238, 234],
        o: [38, 42, 48], d: [176, 182, 188],
      };
      const rows = spec.pattern === "toiletTop" ? top : side;
      for (let y = 0; y < TILE_PX; y++) {
        const row = rows[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "orcSabre":
    case "orcSabreRude": {
      const blade = [
        "..............bb",
        ".............bss",
        "............bsse",
        "...........bsse.",
        "..........bsse..",
        ".........bsse...",
        "........bsse....",
        ".......bsse.....",
        "......bsse......",
        ".....bsse.......",
        "....bsse........",
        "...gggg.........",
        "..gwwg..........",
        "..gwg...........",
        ".gwg............",
        "gg..............",
      ];
      const rude = [
        "..............pp",
        ".............ppl",
        "............pplp",
        "...........pplp.",
        "..........pplp..",
        ".........pplp...",
        "........pplp....",
        ".......pplp.....",
        "......pplp......",
        ".....pplp.......",
        "....pplp........",
        "...pplp.........",
        "..kkkp..........",
        "..kkk...........",
        ".kkk............",
        "kk..............",
      ];
      const colours: Record<string, [number, number, number]> = {
        s: [206, 210, 216], e: [140, 146, 154], b: [238, 242, 248],
        g: [96, 70, 44], w: [186, 150, 84],
        p: [126, 58, 158], l: [168, 104, 196], k: [26, 24, 28],
      };
      const rows = spec.pattern === "orcSabreRude" ? rude : blade;
      for (let y = 0; y < TILE_PX; y++) {
        const row = rows[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "creature": {
      /*
       * A face, drawn pixel by pixel from a mask.
       *
       * The same trick the tools and the зірка use, and for the same reason:
       * at sixteen pixels nothing computed reads as a living thing. Each mask
       * is keyed by a letter into a small palette, so a пятачок and a pair of
       * eyes are two characters apart in the source and you can see the face
       * in the file.
       */
      const FACES: Record<string, { rows: readonly string[]; colours: Record<string, [number, number, number]> }> = {
        pig: {
          rows: [
            "................",
            "..ee........ee..",
            "..eee......eee..",
            "..pppppppppppp..",
            ".pppppppppppppp.",
            ".pppppppppppppp.",
            ".ppwwppppppwwpp.",
            ".ppwwppppppwwpp.",
            ".pppppppppppppp.",
            ".ppppssssssppppp",
            ".ppppsnnnnsppppp",
            ".ppppsnssnsppppp",
            ".ppppssssssppppp",
            ".pppppppppppppp.",
            "..pppppppppppp..",
            "................",
          ],
          colours: {
            p: [226, 158, 162], e: [198, 126, 132], w: [40, 30, 30],
            s: [206, 132, 138], n: [120, 70, 78],
          },
        },
        tur: {
          rows: [
            "................",
            ".hh..........hh.",
            "hhhh........hhhh",
            ".hhh..bbbb..hhh.",
            "..hhbbbbbbbbhh..",
            "...bbbbbbbbbb...",
            "...bwwbbbbwwb...",
            "...bwwbbbbwwb...",
            "...bbbbbbbbbb...",
            "...bbbbbbbbbb...",
            "....bbnnnnbb....",
            "....bnnnnnnb....",
            "....bnssssnb....",
            "....bbnnnnbb....",
            ".....bbbbbb.....",
            "................",
          ],
          colours: {
            b: [92, 68, 52], h: [214, 206, 186], w: [30, 24, 20],
            n: [64, 46, 38], s: [40, 30, 26],
          },
        },
        kurka: {
          /*
           * A hen's head, and a hen's head is all гребінь and beak.
           *
           * The глиняста's clay-brown feathers, the чубата's tuft behind the
           * comb, a red comb notched along the top, and the yellow beak and
           * red wattle underneath. At sixteen pixels those four things are
           * the bird - take the comb off and it is a sparrow.
           */
          rows: [
            "................",
            ".....ff..rr.....",
            "....ffff.r.r....",
            "...ffffrrrrrr...",
            "....cccccccc....",
            "...cccccccccc...",
            "...cwwccccwwc...",
            "...cwwccccwwc...",
            "...cccccccccc...",
            "....ccccccbb....",
            ".....ccccbbbb...",
            "....ccccccbb....",
            "....cccccc......",
            ".....crrc.......",
            "......rr........",
            "................",
          ],
          colours: {
            c: [192, 138, 78], f: [220, 171, 108], r: [196, 36, 42],
            b: [232, 180, 53], w: [32, 26, 20],
          },
        },
        sobaka: {
          /*
           * A дворняга's head: drop ears, a long dark muzzle, sandy coat.
           *
           * The ears are what stop it reading as a кіт at this size. They
           * hang down the sides of the head rather than standing off the top
           * of it, and they are a shade darker than the coat, which is what
           * every sandy yard dog in the country looks like.
           */
          rows: [
            "................",
            "..dd........dd..",
            "..dddd....dddd..",
            "..dddssssssddd..",
            "..ddssssssssdd..",
            "..ddssssssssdd..",
            "..dswwsssswwsd..",
            "..dswwsssswwsd..",
            "..ddssssssssdd..",
            "...dssssssssd...",
            "....ssmmmmss....",
            "....smmmmmms....",
            "....smmnnmms....",
            "....smmmmmms....",
            ".....smmmms.....",
            "................",
          ],
          colours: {
            s: [191, 143, 79], d: [107, 74, 42], m: [58, 44, 32],
            n: [30, 24, 20], w: [28, 22, 18],
          },
        },
        kit: {
          /*
           * A cat's head: two triangles on top, and a small pink nose.
           *
           * Pointed ears standing up are the whole silhouette - a grey cat
           * with the ears taken off is a grey anything. The nose is one
           * pixel of pink and it is worth the pixel: it is the only warm
           * colour on the tile and the eye goes straight to it.
           */
          rows: [
            "................",
            "...g........g...",
            "...gg......gg...",
            "..gpgg....ggpg..",
            "..gggggggggggg..",
            ".gggggggggggggg.",
            ".ggeeggggggeegg.",
            ".ggeeggggggeegg.",
            ".gggggggggggggg.",
            ".gggggkkkkgggggg",
            "..ggggknnkgggg..",
            "..ggggkkkkgggg..",
            "...gggggggggg...",
            "....gggggggg....",
            "......gggg......",
            "................",
          ],
          colours: {
            g: [138, 132, 128], k: [196, 190, 182], n: [216, 150, 156],
            p: [200, 140, 146], e: [176, 196, 96],
          },
        },
        karas: {
          rows: [
            "................",
            "................",
            ".....gggggg.....",
            "...gggggggggg...",
            "..ggggggggggggt.",
            ".gggwgggggggttt.",
            ".ggwwggggggtttt.",
            "ggggggggggggttt.",
            "ggggggggggggttt.",
            ".ggggggggggtttt.",
            ".gggggggggggttt.",
            "..ggggggggggggt.",
            "...gggggggggg...",
            ".....gggggg.....",
            "................",
            "................",
          ],
          colours: {
            g: [176, 168, 108], w: [28, 26, 22], t: [140, 132, 84],
          },
        },
        orc: {
          rows: [
            "................",
            "...mmmmmmmmmm...",
            "..mmmmmmmmmmmm..",
            "..mmmmmmmmmmmm..",
            "..gggggggggggg..",
            ".gggggggggggggg.",
            ".ggrrggggggrrgg.",
            ".ggrrggggggrrgg.",
            ".gggggggggggggg.",
            ".ggggttttttgggg.",
            ".ggggtggggtgggg.",
            ".ggggttttttgggg.",
            "..gggwggggwggg..",
            "..gggggggggggg..",
            "...gggggggggg...",
            "................",
          ],
          colours: {
            g: [104, 128, 74], m: [96, 96, 104], r: [180, 48, 40],
            t: [78, 96, 56], w: [232, 226, 208],
          },
        },
        selianyn: {
          rows: [
            "................",
            "....tttttttt....",
            "...tttttttttt...",
            "tttttttttttttttt",
            "..dddddddddddd..",
            "..ssssssssssss..",
            "..sseessseesss..",
            "..sseessseesss..",
            "..ssssssssssss..",
            "..ssssssssssss..",
            "..ssmmmmmmmmss..",
            "..smmmmmmmmmms..",
            "..ssssssssssss..",
            "...ssssssssss...",
            "....llllllll....",
            "................",
          ],
          colours: {
            s: [214, 168, 130], e: [50, 38, 32], t: [206, 178, 106],
            // The shadow the brim throws, which is what stops a бриль
            // reading as a head of blond hair.
            d: [150, 116, 84], m: [110, 82, 58], l: [236, 232, 220],
          },
        },
        selianka: {
          rows: [
            "................",
            "...rrrrrrrrrr...",
            "..rrrrrrrrrrrr..",
            ".rrrrrrrrrrrrrr.",
            ".rrssssssssssrr.",
            "..ssssssssssss..",
            "..sseessseesss..",
            "..sseessseesss..",
            "..ssssssssssss..",
            "..sssssppsssss..",
            "..ssssssssssss..",
            "...ssssssssss...",
            "....cccccccc....",
            "...cccccccccc...",
            "....llllllll....",
            "................",
          ],
          colours: {
            s: [220, 178, 142], e: [50, 38, 32], r: [190, 54, 52],
            p: [178, 96, 92], c: [198, 60, 58], l: [236, 232, 220],
          },
        },
      };

      const face = FACES[spec.beast ?? "pig"];
      if (face !== undefined) {
        for (let y = 0; y < TILE_PX; y++) {
          const row = face.rows[y] ?? "";
          for (let x = 0; x < TILE_PX; x++) {
            const colour = face.colours[row[x] ?? "."];
            if (colour !== undefined) draw(x, y, colour[0], colour[1], colour[2]);
          }
        }
      }
      break;
    }
    case "roofTile": {
      /*
       * Glazed roof tile: rows of scallops, each row offset by half a tile.
       *
       * Drawn as a joint above every scale and a highlight along its lower
       * curve. Without the highlight it is a grid of dark lines and reads as
       * netting - the curve is what says the surface is made of overlapping
       * pieces rather than scored flat.
       */
      const ROW = 4;
      const WIDE = 6;
      for (let y = 0; y < TILE_PX; y++) {
        const row = Math.floor(y / ROW);
        const withinRow = y - row * ROW;
        const offset = row % 2 === 0 ? 0 : WIDE / 2;
        for (let x = 0; x < TILE_PX; x++) {
          const acrossScale = (x + offset) % WIDE;
          // The joint between one course and the next.
          if (withinRow === 0) { set(x, y, 0.8); continue; }
          /*
           * The seam between two scales, stopped short of the bottom.
           *
           * Run full height it makes a rectangle, and the tile came out
           * looking like green brick - which is both wrong and the one thing
           * this game is not allowed to look like. Ending the seam a pixel
           * early rounds the foot of each scale.
           */
          if (acrossScale === 0 && withinRow < ROW - 1) { set(x, y, 0.85); continue; }
          // The lower edge catches the light. Painted outright rather than
          // mixed toward `line`, which is the dark joint colour - `set` only
          // blends one way, and a highlight is the other way.
          if (withinRow === ROW - 1 && acrossScale >= 1 && acrossScale <= 4) {
            draw(x, y, spec.base[0] + 40, spec.base[1] + 54, spec.base[2] + 42);
          }
        }
      }
      break;
    }
    case "gilt": {
      /*
       * Gilded sheet, the way a dome is actually covered: narrow gores
       * running top to bottom, each one hammered and catching the light down
       * one side.
       *
       * Flat gold reads as yellow plastic. The ribs are the whole difference.
       */
      const GORE = 4;
      for (let x = 0; x < TILE_PX; x++) {
        const within = x % GORE;
        for (let y = 0; y < TILE_PX; y++) {
          if (within === 0) { set(x, y, 0.72); continue; }
          // The lit side of each gore, and the shaded one before the next
          // seam. Gold has to go brighter than its base or it is mustard.
          if (within === 1) draw(x, y, 248, 224, 150);
          else if (within === GORE - 1) set(x, y, 0.45);
        }
      }
      break;
    }
    case "dynamite": {
      /*
       * A stick of dynamite with a fuse, hand-set.
       *
       * Sixteen pixels across is too coarse for a formula - the same lesson
       * the зірка taught. `#` is the stick, `-` its two bands, `/` the fuse
       * and `*` the spark on the end of it.
       */
      const MASK = [
        "................",
        "............*...",
        "...........*....",
        "..........//....",
        ".........//.....",
        "........//......",
        "....######......",
        "....######......",
        "....--####......",
        "....######......",
        "....######......",
        "....--####......",
        "....######......",
        "....######......",
        "................",
        "................",
      ];
      const stick: [number, number, number] = spec.line ?? [176, 52, 46];
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = row[x];
          if (c === "#") draw(x, y, stick[0], stick[1], stick[2]);
          // The bands are the same red gone dark, not a third colour: two
          // reds read as one object, red and brown read as two.
          else if (c === "-") {
            draw(x, y, stick[0] * 0.55, stick[1] * 0.4, stick[2] * 0.4);
          } else if (c === "/") draw(x, y, 210, 190, 150);
          else if (c === "*") draw(x, y, 255, 226, 120);
        }
      }
      break;
    }
    case "star": {
      /*
       * A five-pointed star, drawn rather than computed.
       *
       * Sixteen pixels across is too coarse for any formula to land on a
       * shape a person reads as a star: every version by arithmetic came out
       * a blob with bumps. Hand-set pixels are the only thing that works at
       * this size, and this is the same trick the tools and the помідор use.
       */
      const MASK = [
        "................",
        ".......##.......",
        ".......##.......",
        "......####......",
        "..############..",
        "...##########...",
        "....########....",
        "....########....",
        "....########....",
        "...##########...",
        "...###....###...",
        "..###......###..",
        "..##........##..",
        ".##..........##.",
        "................",
        "................",
      ];
      const star: [number, number, number] = spec.line ?? [232, 74, 68];
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          if (row[x] === "#") draw(x, y, star[0], star[1], star[2]);
        }
      }
      break;
    }
    case "beetCrop": {
      /*
       * Буряк, sown: a fan of leaves from one crown, and red at the foot of
       * it when it is ready.
       *
       * Told apart from wheat at a glance on purpose - the two grow side by
       * side in every город in the game. Wheat is a stand of thin vertical
       * stalks going gold; this is one squat plant with broad leaves leaning
       * out of the middle of it.
       *
       * The first version drew each leaf as a bar on top of a straight stem
       * and came out as a row of small palm trees. Leaves lean; a beet is a
       * fan, not a stand.
       */
      const stage = spec.cropStage ?? 0;
      const tall = [4, 8, 11][stage]!;
      const leaf: [number, number, number] =
        stage === 0 ? [122, 164, 88] : [78, 120, 64];
      const leafDark: [number, number, number] = [56, 92, 48];
      const stem: [number, number, number] = [156, 52, 68];
      const cx = 8;
      const base = TILE_PX - 1;
      const lean = [-0.95, -0.5, -0.1, 0.35, 0.85];
      for (let i = 0; i < lean.length; i++) {
        const away = lean[i]! + (noise(i, 0, tile + 61) - 0.5) * 0.25;
        const len = Math.max(2, Math.round(tall * (0.7 + noise(i, 1, tile + 67) * 0.4)));
        let tipX = cx;
        let tipY = base;
        for (let k = 0; k <= len; k++) {
          tipX = cx + Math.round(Math.sin(away) * k);
          tipY = base - Math.round(Math.cos(away) * k);
          draw(tipX, tipY, stem[0], stem[1], stem[2]);
        }
        // The blade: a blob leaning the way the stalk does.
        const wide = stage === 0 ? 1 : 2;
        for (let dy = -wide; dy <= wide; dy++) {
          for (let dx = -wide - 1; dx <= wide + 1; dx++) {
            if (Math.abs(dx) * 2 + Math.abs(dy) * 3 > (wide + 1) * 3) continue;
            const edge = Math.abs(dx) + Math.abs(dy) >= wide + 1;
            const c = edge ? leafDark : leaf;
            draw(tipX + dx, tipY + dy - 1, c[0], c[1], c[2]);
          }
        }
      }
      // Ripe: the shoulder of the root itself is out of the ground, and it is
      // the whole tell from across a garden.
      if (stage === 2) {
        for (let dy = 0; dy < 4; dy++) {
          const halfWide = 3 - dy;
          for (let dx = -halfWide; dx <= halfWide; dx++) {
            const deep = dy >= 2 || Math.abs(dx) === halfWide;
            draw(cx + dx, base - dy,
              deep ? 112 : 150, deep ? 30 : 36, deep ? 46 : 56);
          }
        }
      }
      break;
    }
    case "beet": {
      /*
       * The root, pulled: a dark red bulb, a tail, and leaves still on it.
       *
       * Hand-set, like the tools and the drone: sixteen pixels across is too
       * coarse for a formula to land on a shape anybody reads as a vegetable.
       */
      const MASK = [
        "................",
        "......ll.l......",
        ".....lllll......",
        "....ll.ll.ll....",
        ".....s.ss.s.....",
        "......sss.......",
        "....bbBBBbb.....",
        "...bBBBBBBBb....",
        "..bBBBBBBBBBb...",
        "..bBBBBBBBBBb...",
        "..bBBBBBBBBBb...",
        "...bBBBBBBBb....",
        "....bBBBBBb.....",
        ".....bBBBb......",
        "......btb.......",
        ".......t........",
      ];
      const paint: Record<string, [number, number, number]> = {
        l: [86, 132, 66], s: [156, 52, 68],
        B: [150, 36, 56], b: [110, 24, 42], t: [92, 20, 36],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const colour = paint[row[x] ?? "."];
          if (colour !== undefined) draw(x, y, colour[0], colour[1], colour[2]);
        }
      }
      break;
    }
    case "beetSeeds": {
      // A pinch of them: buckwheat-brown, knobbly, nothing like a wheat grain.
      const body: [number, number, number] = [148, 106, 72];
      const dark: [number, number, number] = [104, 72, 48];
      for (let i = 0; i < 9; i++) {
        const cx = 2 + Math.floor(noise(i, 0, tile + 13) * 12);
        const cy = 3 + Math.floor(noise(i, 1, tile + 19) * 11);
        for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1], [2, 0]]) {
          draw(cx + dx!, cy + dy!, body[0], body[1], body[2]);
        }
        draw(cx + 1, cy + 1, dark[0], dark[1], dark[2]);
      }
      break;
    }
    case "remote": {
      /*
       * Пульт: the handset, seen face on.
       *
       * Two aerials, a screen and two sticks. The shape has to read at
       * sixteen pixels in a belt beside the дрон it flies, so the aerials are
       * the whole silhouette - a rectangle with a screen in it is every
       * other tool in the game.
       *
       * Hand-set pixels rather than arithmetic, like the drone above it.
       */
      const MASK = [
        "................",
        "....#......#....",
        "....#......#....",
        "....#......#....",
        "..cc##cccc##cc..",
        "..cssssssssssc..",
        "..cssssssssssc..",
        "..cssssssssssc..",
        "..cccccccccccc..",
        "..cclccccccdcc..",
        "..cclccccccdcc..",
        "..cccccccccccc..",
        "..cccccccccccc..",
        "..cccccccccccc..",
        "...cccccccccc...",
        "................",
      ];
      const aerial: [number, number, number] = [176, 180, 190];
      const case_: [number, number, number] = [58, 62, 70];
      const screen: [number, number, number] = [96, 220, 180];
      const stick: [number, number, number] = [214, 62, 52];
      const other: [number, number, number] = [96, 220, 108];
      const paint: Record<string, [number, number, number]> = {
        "#": aerial, c: case_, s: screen, l: stick, d: other,
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const colour = paint[row[x] ?? "."];
          if (colour !== undefined) draw(x, y, colour[0], colour[1], colour[2]);
        }
      }
      break;
    }
    case "drone": {
      /*
       * A quadcopter from above.
       *
       * Four rings for the rotors, four arms, a body, and one light - green
       * with power in it and red without, which is the only difference
       * between the two tiles and is the thing you want to see at a glance in
       * a full belt.
       *
       * Hand-set pixels rather than arithmetic, the same as the star and the
       * tools: sixteen across is too coarse for a formula to land on a shape
       * anybody reads as a machine.
       */
      const MASK = [
        "................",
        "..###......###..",
        ".#...#....#...#.",
        ".#...#....#...#.",
        "..###......###..",
        "....a......a....",
        ".....aabbaa.....",
        "....bbBBBBbb....",
        "....bbBLLBbb....",
        ".....aabbaa.....",
        "....a......a....",
        "..###......###..",
        ".#...#....#...#.",
        ".#...#....#...#.",
        "..###......###..",
        "................",
      ];
      const rotor: [number, number, number] = [176, 180, 190];
      const arm: [number, number, number] = [92, 96, 104];
      const body: [number, number, number] = [52, 55, 62];
      const light: [number, number, number] =
        spec.charged === false ? [214, 62, 52] : [96, 220, 108];
      const paint: Record<string, [number, number, number]> = {
        "#": rotor, a: arm, b: body, B: body, L: light,
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const colour = paint[row[x] ?? "."];
          if (colour !== undefined) draw(x, y, colour[0], colour[1], colour[2]);
        }
      }
      break;
    }
    case "battery": {
      // A cell standing on end, with a cap and a band round the middle so it
      // is not mistaken for an ingot.
      const MASK = [
        "................",
        "......cccc......",
        "......cccc......",
        "....bbbbbbbb....",
        "....bbbbbbbb....",
        "....bbbbbbbb....",
        "....ssssssss....",
        "....ssssssss....",
        "....bbbbbbbb....",
        "....bbbbbbbb....",
        "....bbbbbbbb....",
        "....ssssssss....",
        "....bbbbbbbb....",
        "....bbbbbbbb....",
        "....dddddddd....",
        "................",
      ];
      const paint: Record<string, [number, number, number]> = {
        c: [198, 200, 206],
        b: [46, 62, 96],
        s: [222, 176, 62],
        d: [30, 34, 44],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = MASK[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const colour = paint[row[x] ?? "."];
          if (colour !== undefined) draw(x, y, colour[0], colour[1], colour[2]);
        }
      }
      break;
    }
    case "cobble": {
      // Voronoi cells: one jittered seed per grid square, and a pixel is a
      // joint when it is nearly equidistant from its two nearest seeds. A
      // plain grid of lines reads as netting; this reads as stones.
      const CELL = 4;
      const cells = TILE_PX / CELL;

      // Seed position for a cell index that may fall outside the tile. The
      // *position* uses the raw index so cells line up across the edge; the
      // *jitter* uses the wrapped index so the pattern tiles seamlessly.
      const seed = (i: number, j: number): [number, number] => {
        const wi = ((i % cells) + cells) % cells;
        const wj = ((j % cells) + cells) % cells;
        return [
          i * CELL + noise(wi, wj, tile + 7) * CELL,
          j * CELL + noise(wi, wj, tile + 13) * CELL,
        ];
      };

      for (let y = 0; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const ci = Math.floor(x / CELL);
          const cj = Math.floor(y / CELL);

          let nearest = Infinity;
          let second = Infinity;
          for (let dj = -1; dj <= 1; dj++) {
            for (let di = -1; di <= 1; di++) {
              const [sx, sy] = seed(ci + di, cj + dj);
              const d = Math.hypot(x + 0.5 - sx, y + 0.5 - sy);
              if (d < nearest) { second = nearest; nearest = d; }
              else if (d < second) { second = d; }
            }
          }

          // Close to the midline between two stones: that is the joint.
          const edge = second - nearest;
          if (edge < 1.1) set(x, y, 0.75 * (1 - edge / 1.1));
        }
      }
      break;
    }
    case "bark": {
      // Furrows of varying depth, wrapping at the tile edge. Down the tile
      // for a standing trunk; across it for one lying on its side.
      const along = spec.across === true;
      for (let a = 0; a < TILE_PX; a++) {
        const depth = noise(a, 0, tile + 21);
        if (depth > 0.62) {
          for (let b = 0; b < TILE_PX; b++) {
            // Break the furrow up a little so it is not a ruled line.
            const x = along ? b : a;
            const y = along ? a : b;
            if (noise(x, y, tile + 29) > 0.18) set(x, y, 0.55 * depth);
          }
        }
      }
      break;
    }
    case "rings": {
      // Growth rings around an off-centre heart.
      const cx = TILE_PX / 2 + (noise(0, 0, tile + 31) - 0.5) * 3;
      const cy = TILE_PX / 2 + (noise(1, 1, tile + 37) - 0.5) * 3;
      for (let y = 0; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
          // Rings tighten toward the bark, as they do in a real trunk.
          const ring = Math.sin(d * 1.9) * 0.5 + 0.5;
          if (ring > 0.72) set(x, y, (ring - 0.72) * 2.2);
        }
      }
      break;
    }
    case "planks": {
      // Four boards with a seam between them, and staggered end joints.
      for (let y = 0; y < TILE_PX; y++) {
        if (y % 4 === 0) {
          for (let x = 0; x < TILE_PX; x++) set(x, y, 0.9);
          continue;
        }
        // One end joint per board, in a different place on each.
        const board = Math.floor(y / 4);
        const joint = Math.floor(noise(board, 0, tile + 43) * TILE_PX);
        set(joint, y, 0.75);
      }
      break;
    }
    case "wheat": {
      // Stalks of varying height with a heavier ear on top. Drawn on
      // transparent, because this tile is stretched over crossed quads.
      for (let sx = 1; sx < TILE_PX; sx += 3) {
        const jitter = Math.floor(noise(sx, 0, tile + 61) * 2);
        const x = sx + jitter;
        const height = 9 + Math.floor(noise(sx, 1, tile + 67) * 5);
        const base = TILE_PX - 1;
        for (let k = 0; k < height; k++) {
          const y = base - k;
          // Green at the foot fading to ripe gold at the ear.
          const t = k / height;
          draw(x, y,
            108 + t * 100,
            132 + t * 42,
            58 + t * 22);
        }
        // The ear: a couple of pixels wider at the very top.
        const earY = base - height;
        draw(x, earY, line[0], line[1], line[2]);
        draw(x - 1, earY + 1, line[0], line[1], line[2]);
        draw(x + 1, earY + 1, line[0], line[1], line[2]);
      }
      break;
    }
    case "grains": {
      // A handful of wheat, piled: plump oval grains with a crease down the
      // middle, not the scattered specks this was. The item is both what you
      // sow and what you eat, so it should look like grain rather than seed.
      const hi: [number, number, number] = [244, 216, 142];
      const body: [number, number, number] = [217, 180, 94];
      const crease: [number, number, number] = [176, 141, 63];
      const shade: [number, number, number] = [150, 118, 48];

      /**
       * One grain, lying on its side: four wide, three tall, corners cut.
       *
       *    . h h .
       *    e b b e
       *    . s s .
       *
       * Taller than it is wide, with the corners cut, the silhouette comes out
       * a plus sign rather than an oval - which is exactly what the first
       * attempt looked like. Wider than tall is the shape that works.
       */
      const grain = (gx: number, gy: number, tint: number): void => {
        const mix = (
          c: [number, number, number],
        ): [number, number, number] => [
          Math.min(255, c[0] * tint),
          Math.min(255, c[1] * tint),
          Math.min(255, c[2] * tint),
        ];
        const h = mix(hi), b = mix(body), e = mix(crease), sh = mix(shade);

        draw(gx + 1, gy, h[0], h[1], h[2]);
        draw(gx + 2, gy, h[0], h[1], h[2]);

        draw(gx, gy + 1, e[0], e[1], e[2]);
        draw(gx + 1, gy + 1, b[0], b[1], b[2]);
        draw(gx + 2, gy + 1, b[0], b[1], b[2]);
        draw(gx + 3, gy + 1, e[0], e[1], e[2]);

        draw(gx + 1, gy + 2, sh[0], sh[1], sh[2]);
        draw(gx + 2, gy + 2, sh[0], sh[1], sh[2]);
      };

      // Piled: one on top, two, then three across the bottom.
      const pile: [number, number][] = [
        [6, 3],
        [3, 7], [9, 7],
        [1, 11], [6, 11], [11, 11],
      ];
      pile.forEach(([gx, gy], i) => {
        // A little variation so the pile is not six identical stamps.
        grain(gx, gy, 0.9 + noise(i, 0, tile + 101) * 0.2);
      });
      break;
    }
    case "door": {
      const upper = spec.doorHalf === "upper";
      const framed = spec.doorStyle === "panelled";

      // Vertical boards, the same on both halves so a door reads as one plank
      // door rather than as two blocks stacked.
      for (let x = 0; x < TILE_PX; x++) {
        if (x % 5 === 0) {
          for (let y = 0; y < TILE_PX; y++) set(x, y, 0.7);
        }
      }

      if (framed) {
        // A recessed panel: an inset rectangle, lit on top and left.
        const x0 = 3, x1 = 12;
        const y0 = upper ? 3 : 2;
        const y1 = upper ? 13 : 12;
        for (let x = x0; x <= x1; x++) {
          set(x, y0, 0.8);
          set(x, y1, 0.45);
        }
        for (let y = y0; y <= y1; y++) {
          set(x0, y, 0.8);
          set(x1, y, 0.45);
        }
      } else {
        // Iron straps: two heavy bands, and a diagonal brace between them on
        // the lower half, which is where a braced door carries its load.
        const bands = upper ? [2, 12] : [3, 13];
        for (const by of bands) {
          for (let x = 1; x < TILE_PX - 1; x++) {
            set(x, by, 0.9);
            set(x, by + 1, 0.55);
          }
        }
        if (!upper) {
          for (let k = 0; k < 9; k++) set(3 + k, 12 - k, 0.75);
        }
      }

      // The handle, on the side away from the hinge, on the lower half only.
      if (!upper) {
        for (let y = 6; y <= 8; y++) set(13, y, 0.95);
        set(12, 7, 0.95);
      }
      break;
    }
    case "crop": {
      // Sown wheat, in rows rather than the loose clumps of the wild kind.
      const stage = spec.cropStage ?? 0;
      const height = [4, 9, 14][stage]!;
      // Green shoots, still green but tall, then ripe gold.
      const tip: [number, number, number] =
        stage === 0 ? [126, 168, 74]
        : stage === 1 ? [148, 178, 70]
        : [214, 182, 82];
      const root: [number, number, number] = [92, 124, 54];

      for (let sx = 2; sx < TILE_PX; sx += 4) {
        const x = sx + Math.floor(noise(sx, 0, tile + 91) * 2);
        const h = height - Math.floor(noise(sx, 1, tile + 97) * 2);
        for (let k = 0; k < h; k++) {
          const y = TILE_PX - 1 - k;
          const t = h <= 1 ? 1 : k / (h - 1);
          draw(x, y,
            root[0] + (tip[0] - root[0]) * t,
            root[1] + (tip[1] - root[1]) * t,
            root[2] + (tip[2] - root[2]) * t);
        }
        // Only ripe wheat carries an ear heavy enough to see.
        if (stage === 2) {
          const earY = TILE_PX - h;
          draw(x, earY, 226, 194, 92);
          draw(x - 1, earY + 1, 200, 168, 76);
          draw(x + 1, earY + 1, 200, 168, 76);
          draw(x, earY + 1, 226, 194, 92);
        }
      }
      break;
    }
    case "glass": {
      // A brighter rim, because the edge of a pane catches light, and one
      // diagonal streak so a wall of glass is not a flat wash.
      for (let k = 0; k < TILE_PX; k++) {
        set(k, 0, 0.8);
        set(k, TILE_PX - 1, 0.8);
        set(0, k, 0.8);
        set(TILE_PX - 1, k, 0.8);
      }
      for (let k = 3; k < 11; k++) set(k, 12 - k, 0.55);
      break;
    }
    case "window": {
      // Frame and mullion are opaque; the four panes stay translucent. `draw`
      // writes full alpha, which is what separates the timber from the glass.
      const f = spec.frame ?? [140, 100, 58];
      const dark: [number, number, number] = [f[0] * 0.7, f[1] * 0.7, f[2] * 0.7];
      const lit: [number, number, number] = [
        Math.min(255, f[0] * 1.18), Math.min(255, f[1] * 1.18), Math.min(255, f[2] * 1.18),
      ];

      const timber = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      // Two-pixel frame around the whole pane.
      for (let k = 0; k < TILE_PX; k++) {
        timber(k, 0, lit);
        timber(k, 1, f);
        timber(k, TILE_PX - 2, f);
        timber(k, TILE_PX - 1, dark);
        timber(0, k, lit);
        timber(1, k, f);
        timber(TILE_PX - 2, k, f);
        timber(TILE_PX - 1, k, dark);
      }
      // The mullion, splitting it into four panes as the reference window is.
      for (let k = 2; k < TILE_PX - 2; k++) {
        timber(7, k, f);
        timber(8, k, dark);
        timber(k, 7, f);
        timber(k, 8, dark);
      }
      // A highlight in each pane, so the glass reads as glass.
      for (const [px, py] of [[3, 3], [10, 3], [3, 10], [10, 10]]) {
        set(px!, py!, 0.5);
        set(px! + 1, py! + 1, 0.35);
      }
      break;
    }
    case "stoveSide":
    case "stoveFront":
    case "stoveTop": {
      const painted = spec.painted === true;
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      // The folk palette off the reference: green vine, orange bloom, yellow.
      const green: [number, number, number] = [96, 148, 92];
      const orange: [number, number, number] = [222, 132, 62];
      const yellow: [number, number, number] = [236, 190, 84];
      const soot: [number, number, number] = [38, 32, 30];
      const ember: [number, number, number] = [176, 74, 38];

      /**
       * The painted band.
       *
       * A rooster is not going to survive sixteen pixels, so what carries the
       * reference is its palette and rhythm: a green vine with a bloom every
       * few pixels. Read across a whole stove it says the same thing.
       */
      const vine = (y: number): void => {
        for (let x = 0; x < TILE_PX; x++) {
          const wave = y + (Math.sin(x * 0.9) > 0 ? 0 : 1);
          put3(x, wave, green);
        }
        for (let x = 2; x < TILE_PX; x += 6) {
          put3(x, y - 1, orange);
          put3(x + 1, y - 1, yellow);
          put3(x, y + 2, yellow);
        }
      };

      if (spec.pattern === "stoveTop") {
        // A smooth plate: courses of the same material, worn flat.
        for (let y = 0; y < TILE_PX; y += 6) {
          for (let x = 0; x < TILE_PX; x++) set(x, y, 0.55);
        }
        if (painted) vine(8);
        break;
      }

      // Both the side and the front carry the mantel band near the top.
      for (let x = 0; x < TILE_PX; x++) {
        set(x, 3, 0.8);
        set(x, 4, 0.5);
      }
      if (painted) vine(1);

      if (spec.pattern === "stoveSide") {
        // Coursed clay below the mantel.
        for (let y = 7; y < TILE_PX; y += 4) {
          for (let x = 0; x < TILE_PX; x++) set(x, y, 0.45);
        }
        if (painted) {
          // A bloom in each lower corner, where the reference puts its birds.
          for (const cx of [2, 12]) {
            put3(cx, 9, orange);
            put3(cx + 1, 9, yellow);
            put3(cx, 10, green);
            put3(cx + 1, 11, green);
          }
        }
        break;
      }

      // The firebox: an arched opening, sooty, with embers at its foot.
      const archTop = 7;
      const lit = spec.burning === true;
      for (let y = archTop; y < TILE_PX - 1; y++) {
        // The arch narrows toward its crown.
        const inset = y === archTop ? 5 : y === archTop + 1 ? 4 : 3;
        for (let x = inset; x < TILE_PX - inset; x++) put3(x, y, soot);
      }
      if (lit) {
        /*
         * A fire in the mouth.
         *
         * Drawn inside the arch that is already there rather than over it, so
         * the cold stove and the burning one are the same stove: the opening
         * does not move, the soot round its edge stays, and only what is in
         * it changes. A separate drawing would read as a different block.
         */
        const flame: [number, number, number] = [232, 128, 34];
        const flameHot: [number, number, number] = [250, 204, 96];
        const flameDeep: [number, number, number] = [186, 58, 22];

        // Logs across the floor of the box, which is what the fire sits on.
        for (let x = 5; x < TILE_PX - 5; x++) put3(x, TILE_PX - 2, flameDeep);
        // The body of the fire, tallest in the middle and ragged at the top -
        // a flat block of orange reads as a painted panel, not as flame.
        const height = [1, 3, 4, 3, 4, 2] as const;
        for (let i = 0; i < height.length; i++) {
          const x = 5 + i;
          for (let k = 0; k < height[i]!; k++) {
            const y = TILE_PX - 3 - k;
            if (y < archTop + 1) continue;
            put3(x, y, k === 0 ? flame : k === height[i]! - 1 ? flameDeep : flameHot);
          }
        }
        // And the glow spilling onto the lip of the arch.
        put3(4, TILE_PX - 2, ember);
        put3(TILE_PX - 5, TILE_PX - 2, ember);
      } else {
        for (let x = 5; x < TILE_PX - 5; x++) {
          if (noise(x, 0, tile + 131) > 0.55) put3(x, TILE_PX - 2, ember);
        }
      }
      if (painted) {
        for (const cx of [1, 13]) {
          put3(cx, 9, orange);
          put3(cx + 1, 10, yellow);
          put3(cx, 11, green);
        }
      }
      break;
    }
    case "shutter": {
      // Vertical boards held by a ledge top and bottom - a ledged shutter,
      // which is what hangs beside a хата window.
      for (let x = 0; x < TILE_PX; x++) {
        if (x % 4 === 0) {
          for (let y = 0; y < TILE_PX; y++) set(x, y, 0.75);
        }
      }
      for (const ly of [1, 2, TILE_PX - 3, TILE_PX - 2]) {
        for (let x = 0; x < TILE_PX; x++) set(x, ly, ly < 4 ? 0.85 : 0.6);
      }

      // The cut-out heart. A shutter without one is a plank; with one it is
      // unmistakably a shutter, and it is the detail the reference carries.
      const heart: [number, number][] = [
        [7, 6], [8, 6], [6, 7], [7, 7], [8, 7], [9, 7],
        [6, 8], [7, 8], [8, 8], [9, 8], [7, 9], [8, 9], [7, 10], [8, 10],
      ];
      for (const [hx, hy] of heart) set(hx, hy, 1);
      break;
    }
    case "bedPillow": {
      // A pillow in unbleached linen, with a stitched border along its edges.
      for (let x = 0; x < TILE_PX; x++) {
        set(x, 0, 0.4);
        set(x, TILE_PX - 1, 0.55);
      }
      for (let y = 0; y < TILE_PX; y++) {
        set(0, y, 0.4);
        set(TILE_PX - 1, y, 0.55);
      }
      stitchBand(draw, 3, STITCH_RED, STITCH_BLACK);
      // A crease down the middle, so it reads as stuffed rather than flat.
      for (let x = 3; x < TILE_PX - 3; x++) set(x, 10, 0.3);
      break;
    }
    case "bedCover": {
      // The coverlet: two stitched bands with plain linen between them.
      stitchBand(draw, 4, STITCH_RED, STITCH_BLACK);
      stitchBand(draw, 11, STITCH_BLACK, STITCH_RED);
      // A woven grain across the linen between the bands.
      for (let y = 7; y <= 8; y++) {
        for (let x = 0; x < TILE_PX; x += 3) set(x, y, 0.35);
      }
      break;
    }
    case "bedSide": {
      // Coverlet hanging over a timber rail: cloth on top, wood below.
      const rail = spec.frame ?? [140, 100, 58];
      for (let y = 9; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const shade = y === 9 ? 1.15 : y === TILE_PX - 1 ? 0.7 : 1;
          draw(x, y,
            Math.min(255, rail[0] * shade),
            Math.min(255, rail[1] * shade),
            Math.min(255, rail[2] * shade));
        }
      }
      stitchBand(draw, 4, STITCH_RED, STITCH_BLACK);
      // The fringe where the cloth meets the wood.
      for (let x = 0; x < TILE_PX; x += 2) set(x, 8, 0.45);
      break;
    }
    case "bedEnd": {
      // A carved board: vertical planks with a turned rail across the top.
      for (let x = 0; x < TILE_PX; x++) {
        if (x % 5 === 0) {
          for (let y = 0; y < TILE_PX; y++) set(x, y, 0.7);
        }
      }
      for (const ry of [2, 3]) {
        for (let x = 0; x < TILE_PX; x++) set(x, ry, ry === 2 ? 0.85 : 0.55);
      }
      // A row of drilled holes, the way a хата bedhead is cut.
      for (let x = 3; x < TILE_PX - 2; x += 4) {
        set(x, 7, 0.9);
        set(x + 1, 7, 0.9);
        set(x, 8, 0.9);
        set(x + 1, 8, 0.9);
      }
      break;
    }
    case "vatSide": {
      // Hooped staves, the way a tub is bound. Fired clay, not wood.
      for (const hy of [2, 3, TILE_PX - 4, TILE_PX - 3]) {
        for (let x = 0; x < TILE_PX; x++) set(x, hy, hy < 5 ? 0.75 : 0.6);
      }
      for (let x = 0; x < TILE_PX; x += 5) {
        for (let y = 0; y < TILE_PX; y++) set(x, y, 0.35);
      }
      break;
    }
    case "vatTop": {
      // A thick rim with the hollow inside it, or the water it is holding.
      const water: [number, number, number] = [64, 116, 162];
      for (let y = 0; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const edge = x < 2 || y < 2 || x >= TILE_PX - 2 || y >= TILE_PX - 2;
          if (edge) continue;
          if (spec.filled === true) {
            const ripple = noise(x, y, tile + 151) * 0.22 + 0.89;
            draw(x, y,
              Math.min(255, water[0] * ripple),
              Math.min(255, water[1] * ripple),
              Math.min(255, water[2] * ripple));
          } else {
            // The dark of an empty tub.
            set(x, y, 0.72);
          }
        }
      }
      // A lip of light along the near edge of the rim.
      for (let x = 0; x < TILE_PX; x++) set(x, 0, 0.25);
      break;
    }
    case "bucket": {
      // A tapered clay pail with a rope bail over it.
      const clay = line;
      const dark: [number, number, number] = [clay[0] * 0.7, clay[1] * 0.7, clay[2] * 0.7];
      const lit: [number, number, number] = [
        Math.min(255, clay[0] * 1.2), Math.min(255, clay[1] * 1.2),
        Math.min(255, clay[2] * 1.2),
      ];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      // Bail: an arch above the rim.
      for (let x = 5; x <= 10; x++) put3(x, 2, dark);
      put3(4, 3, dark);
      put3(11, 3, dark);

      // Body, tapering toward the base.
      for (let y = 5; y < 14; y++) {
        const inset = y < 12 ? 3 : 4;
        for (let x = inset; x < TILE_PX - inset; x++) {
          put3(x, y, y === 5 ? lit : y >= 12 ? dark : clay);
        }
      }
      if (spec.filled === true) {
        const water: [number, number, number] = [72, 128, 176];
        for (let x = 4; x < TILE_PX - 4; x++) put3(x, 6, water);
        for (let x = 4; x < TILE_PX - 4; x++) {
          put3(x, 7, [water[0] * 0.85, water[1] * 0.85, water[2] * 0.85]);
        }
      }
      break;
    }
    case "flax": {
      // Slender stalks, finer than wheat and closer together, with a spray of
      // pale blue flowers once they are up.
      const stage = spec.cropStage ?? 0;
      const height = [4, 10, 14][stage]!;
      const stem: [number, number, number] =
        stage === 0 ? [118, 158, 96] : [136, 166, 104];
      const flower: [number, number, number] = [126, 158, 214];
      const flowerLit: [number, number, number] = [178, 202, 240];

      for (let sx = 1; sx < TILE_PX; sx += 3) {
        const x = sx + Math.floor(noise(sx, 0, tile + 161) * 2);
        const h = height - Math.floor(noise(sx, 1, tile + 167) * 3);
        for (let k = 0; k < h; k++) {
          draw(x, TILE_PX - 1 - k, stem[0], stem[1], stem[2]);
        }
        if (stage === 2) {
          const fy = TILE_PX - h;
          draw(x, fy, flowerLit[0], flowerLit[1], flowerLit[2]);
          draw(x - 1, fy + 1, flower[0], flower[1], flower[2]);
          draw(x + 1, fy + 1, flower[0], flower[1], flower[2]);
        }
      }
      break;
    }
    case "pickaxe": {
      // Head at the foot of the shaft, like the hoe, so the two read as tools
      // from the same hand. What tells a pick from a hoe at 16 px is the
      // silhouette: an arc opening downward, thick where the shaft meets it
      // and sharp at both tips.
      //
      // Laid out pixel by pixel rather than from a curve. Two parametric
      // versions came out as a footbridge and then as a tent - at this size
      // the difference between a tool and a shape is four or five pixels, and
      // a formula spends them where it likes.
      // Same silhouette in iron, only recoloured: it is the same tool, and a
      // different outline would say it was a different one. `line` carries the
      // metal, so the two picks are one drawing and one decision.
      const head: [number, number, number] = spec.line ?? [190, 146, 86];
      const headLit: [number, number, number] = shade(head, 1.35);
      const headDark: [number, number, number] = shade(head, 0.68);
      const cord: [number, number, number] = [96, 78, 50];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put3(x, y, c);
      };

      drawShaft(draw, 10);
      put3(8, 9, headDark);
      put3(8, 10, cord);

      /*
       * The points curve back up, towards the handle.
       *
       * They curved away from it - the arc opened downward, so the haft
       * met the head on its convex side, which is a pick mounted
       * backwards: "залізна частина прикріплена наружною частиною". A
       * pick's points curve towards whoever is holding it.
       */
      span(12, 6, 10, headLit);   // crown, where the haft goes through
      span(11, 5, 11, head);
      put3(4, 10, head);   put3(12, 10, head);
      put3(3, 9, head);    put3(13, 9, head);
      put3(3, 8, headDark); put3(13, 8, headDark);
      span(13, 7, 9, headDark);   // shadow under the crown
      break;
    }
    case "axe": {
      /*
       * A fan: narrow at the eye, wide at the edge, all on one side.
       *
       * Three drafts to get here and the first two are worth naming, because
       * both were *symmetrical blocks* of pixels and both came out as
       * kitchenware - a mallet, then a mug. What makes a сокира a сокира at
       * sixteen pixels is the taper: two pixels tall where the haft goes
       * through it and six at the cutting edge, so the head reads as a wedge
       * driven into wood rather than as a weight on a stick. The pick beside
       * it is the opposite shape - symmetrical, opening downward - which is
       * how the two tell each other apart in a belt.
       *
       * Left-handed: the haft comes down from the top right, so the head
       * hangs left or it grows out of the tile.
       */
      const head: [number, number, number] = spec.line ?? [190, 146, 86];
      const headLit: [number, number, number] = shade(head, 1.4);
      const headDark: [number, number, number] = shade(head, 0.66);
      const cord: [number, number, number] = [96, 78, 50];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      drawShaft(draw);
      // The binding where the haft enters the eye.
      put3(8, 9, cord);

      /*
       * The head, column by column: how tall it is at each step out from
       * the haft. Written this way round because the taper is the drawing.
       */
      const row: readonly (readonly [number, number, number])[] = [
        // y, from x, to x - widest under the haft, tapering to the heel.
        [9, 3, 8],
        [10, 3, 8],
        [11, 3, 7],
        [12, 3, 6],
        [13, 3, 5],
        [14, 3, 4],
      ];
      for (const [y, from, to] of row) {
        for (let x = from; x <= to; x++) {
          // Darker along the back, so the head reads as thick there and
          // thin at the edge rather than as a flat patch of colour.
          put3(x, y, x >= to - 1 ? headDark : head);
        }
      }
      /*
       * And the edge: bellied, not straight.
       *
       * Two pixels proud in the middle and none at the ends is the curve a
       * sharpened axe carries. Three drafts before this one were
       * symmetrical lumps around the haft and came out as kitchenware - a
       * mallet, then twice a mug. The taper *is* the drawing.
       */
      for (let y = 10; y <= 12; y++) put3(2, y, headLit);
      put3(3, 9, headLit);
      put3(3, 13, headLit);
      put3(3, 14, headLit);
      break;
    }
    case "shovel": {
      // Straight sides drawn in to a point: a spade. Five pixels wide, six
      // tall. Seven wide with a flat foot came out as the clay bucket, and
      // an outlined rectangle came out as a crate.
      const blade: [number, number, number] = [182, 138, 80];
      const bladeLit: [number, number, number] = [212, 166, 104];
      const bladeDark: [number, number, number] = [130, 92, 50];
      const cord: [number, number, number] = [96, 78, 50];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put3(x, y, c);
      };

      drawShaft(draw);
      put3(8, 8, bladeDark);
      put3(8, 9, cord);

      span(10, 7, 9, bladeLit);
      span(11, 6, 10, blade);
      span(12, 6, 10, blade);
      span(13, 6, 10, bladeDark);
      span(14, 7, 9, bladeDark);
      put3(8, 15, bladeDark);
      // Edges, so the blade has a rim rather than being a flat patch.
      put3(6, 11, bladeDark); put3(10, 11, bladeDark);
      put3(6, 12, bladeDark); put3(10, 12, bladeDark);
      break;
    }
    case "sabre": {
      // The one shape here that is not a shaft with something on the end. A
      // шабля is its curve - straighten it and it is a sword, which is a
      // different weapon from a different army - and the curve has to
      // accelerate, steeper near the tip than near the hand.
      // Same curve in steel, only recoloured - the same way the two picks
      // are one drawing. A шабля is its shape; a different outline would say
      // it was a different weapon rather than a better one.
      const wood: [number, number, number] = spec.line ?? [212, 178, 120];
      const woodLit: [number, number, number] = shade(wood, 1.16);
      const edge: [number, number, number] = shade(wood, 0.72);
      const grip: [number, number, number] = [82, 58, 34];
      const guard: [number, number, number] = shade(wood, 0.69);
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      // Grip: two pixels wide and dark, at the bottom left where the hand is.
      for (let y = 12; y <= 15; y++) { put3(1, y, grip); put3(2, y, grip); }
      // Guard: across the blade's root, so hand and blade are visibly two
      // things. One pixel of brown near the blade's own colour was invisible.
      put3(3, 11, guard); put3(4, 11, guard);
      put3(2, 11, guard); put3(3, 12, guard);

      // Blade, hand-placed. Rising slowly at first, then hard at the tip.
      const spine: readonly (readonly [number, number])[] = [
        [4, 12], [5, 11], [6, 10], [7, 9], [8, 8],
        [9, 7], [10, 6], [11, 4], [12, 3], [13, 2],
      ];
      for (const [x, y] of spine) {
        put3(x, y, wood);
        put3(x, y + 1, edge);
      }
      put3(13, 1, woodLit);
      put3(14, 1, woodLit);
      break;
    }
    case "ironSabre": {
      // The Meshy sabre as a picture: sixteen pixels of what the model is,
      // a checkered steel blade, a tan cross-guard over its root, a dark
      // grip with a grey pommel. Hilt bottom-left, tip top-right, the way
      // every tool here lies. Drawn by hand from a face-on render of the
      // model; an averaged-down render was mud.
      const rows = [
        ".............sll",
        "............sll.",
        "...........sksk.",
        "..........sksk..",
        ".........sksk...",
        "........sksk....",
        ".......sksk.....",
        "..dtd.sksk......",
        "...dtdksk.......",
        "....dtdk........",
        "....gstd........",
        "...gg..td.......",
        "..gg....td......",
        ".gg.............",
        "pp..............",
        "pp..............",
      ];
      const colours: Record<string, [number, number, number]> = {
        k: [30, 30, 32], s: [118, 118, 124], l: [168, 170, 176],
        t: [170, 136, 100], d: [126, 98, 70],
        g: [42, 38, 36], p: [78, 78, 84],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = rows[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "woodenSabre": {
      // The Meshy cutlass as a picture: a curved blade of dark, rust-worn
      // wood with a lighter grain and a worn tip, a tan cross-guard over
      // its root, a dark grip and pommel. Hilt bottom-left, tip top-right,
      // like the iron one and every tool here. Drawn by hand from a
      // face-on render of the model.
      const rows = [
        ".............wee",
        ".............eew",
        "............wew.",
        "...........wmw..",
        "..........wmw...",
        ".........wmww...",
        "........wmw.....",
        "..dtd.wwmw......",
        "...dtdwmw.......",
        "....dtdw........",
        "....gmtd........",
        "...gg..td.......",
        "..gg....td......",
        ".gg.............",
        "pp..............",
        "pp..............",
      ];
      const colours: Record<string, [number, number, number]> = {
        w: [64, 50, 42], m: [100, 80, 66], e: [150, 122, 98],
        t: [196, 160, 124], d: [140, 110, 84],
        g: [32, 26, 24], p: [64, 52, 46],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = rows[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "adamantSabre": {
      // The Meshy scimitar as a picture: a wide pale-stone blade sweeping
      // up at the tip, its back catching the light, a long straight guard
      // and a grip of the same stone. Hilt bottom-left, tip top-right, like
      // the other two. Drawn by hand from a face-on render of the model.
      const rows = [
        ".............mhl",
        "............mhhm",
        "...........mhlm.",
        "..........mhlm..",
        ".........mhlm...",
        "........hhlm....",
        ".......hhlm.....",
        "..mg..hhlm......",
        "...mgmhlm.......",
        "....mgmm........",
        "....dmgm........",
        "...dm..gm.......",
        "..dm....gm......",
        ".dm......g......",
        "gg..............",
        "gg..............",
      ];
      const colours: Record<string, [number, number, number]> = {
        h: [238, 240, 246], l: [214, 214, 222], m: [178, 180, 190],
        d: [126, 128, 140], g: [200, 204, 214],
      };
      for (let y = 0; y < TILE_PX; y++) {
        const row = rows[y] ?? "";
        for (let x = 0; x < TILE_PX; x++) {
          const c = colours[row[x] ?? "."];
          if (c !== undefined) draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "bow": {
      // A limb bent into a C with the string across its mouth. The gap between
      // the two is the whole read: a bow drawn touching its own string is a
      // crescent moon.
      const limb: [number, number, number] = [150, 106, 58];
      const limbLit: [number, number, number] = [186, 138, 78];
      const string: [number, number, number] = [226, 220, 200];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      for (let t = 0; t <= 24; t++) {
        // Half a circle, opening to the right.
        const a = Math.PI * (t / 24) - Math.PI / 2;
        const x = Math.round(11 - 8 * Math.cos(a));
        const y = Math.round(7.5 + 7.5 * Math.sin(a));
        if (x < 0 || x >= TILE_PX || y < 0 || y >= TILE_PX) continue;
        put3(x, y, t > 8 && t < 16 ? limbLit : limb);
      }
      // The string runs between the two tips, not down the whole tile.
      for (let y = 1; y <= 14; y++) put3(11, y, string);
      break;
    }
    case "arrow": {
      // Along the same diagonal every tool here uses, so a quiverful in the
      // hotbar sits at the same angle as the bow beside it.
      const shaft: [number, number, number] = [162, 120, 70];
      const head: [number, number, number] = [148, 152, 158];
      const headLit: [number, number, number] = [196, 200, 206];
      const fletch: [number, number, number] = [214, 206, 178];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      for (let k = 3; k <= 12; k++) put3(k, 15 - k, shaft);

      // Stone head at the top right: a triangle with two barbs.
      put3(13, 2, headLit);
      put3(12, 2, head);
      put3(13, 3, head);
      put3(11, 3, head);
      put3(13, 4, head);
      put3(14, 1, headLit);

      // Fletching at the other end, three feathers off the shaft.
      for (let k = 0; k < 3; k++) {
        put3(3 + k, 15 - k - 1, fletch);
        put3(3 + k + 1, 15 - k, fletch);
      }
      break;
    }
    case "chestSide": {
      // Two iron bands and the seam of the lid. Boards run vertically.
      const iron = line;
      const seam: [number, number, number] = [86, 58, 32];
      for (let x = 0; x < TILE_PX; x += 4) {
        for (let y = 0; y < TILE_PX; y++) draw(x, y, seam[0], seam[1], seam[2]);
      }
      for (let x = 0; x < TILE_PX; x++) {
        for (const y of [4, 5]) draw(x, y, iron[0], iron[1], iron[2]);
        for (const y of [12, 13]) draw(x, y, iron[0], iron[1], iron[2]);
      }
      break;
    }
    case "chestFront": {
      // The same bands, plus the painted flower a скриня carried and a lock
      // plate between them. The flower is what tells the front from the side
      // at a glance, so it goes where the bands leave room.
      const iron = line;
      const seam: [number, number, number] = [86, 58, 32];
      const petal: [number, number, number] = [196, 62, 58];
      const heart: [number, number, number] = [236, 196, 84];
      const leaf: [number, number, number] = [96, 132, 62];

      for (let x = 0; x < TILE_PX; x += 4) {
        for (let y = 0; y < TILE_PX; y++) draw(x, y, seam[0], seam[1], seam[2]);
      }
      for (let x = 0; x < TILE_PX; x++) {
        for (const y of [4, 5]) draw(x, y, iron[0], iron[1], iron[2]);
        for (const y of [12, 13]) draw(x, y, iron[0], iron[1], iron[2]);
      }

      // Flower: four petals round a bright centre, at 8,9.
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      put3(8, 9, heart);
      put3(7, 9, petal); put3(9, 9, petal);
      put3(8, 8, petal); put3(8, 10, petal);
      put3(6, 9, leaf); put3(10, 9, leaf);

      // Lock plate on the lid seam, so the front reads as the opening side.
      for (const x of [7, 8]) { put3(x, 4, [214, 206, 190]); put3(x, 5, [160, 152, 140]); }
      break;
    }
    case "chestTop": {
      // The lid: boards the other way, one band across, and the hasp.
      const iron = line;
      const seam: [number, number, number] = [96, 66, 38];
      for (let y = 0; y < TILE_PX; y += 4) {
        for (let x = 0; x < TILE_PX; x++) draw(x, y, seam[0], seam[1], seam[2]);
      }
      for (let y = 0; y < TILE_PX; y++) {
        for (const x of [4, 5]) draw(x, y, iron[0], iron[1], iron[2]);
        for (const x of [10, 11]) draw(x, y, iron[0], iron[1], iron[2]);
      }
      break;
    }
    case "spawner": {
      // A cage of bars with the mark burning inside it.
      const bar: [number, number, number] = [88, 82, 84];
      const glow = line;
      for (let x = 2; x < TILE_PX - 2; x += 3) {
        for (let y = 2; y < TILE_PX - 2; y++) draw(x, y, bar[0], bar[1], bar[2]);
      }
      for (let x = 2; x < TILE_PX - 2; x++) {
        for (const y of [2, TILE_PX - 3]) draw(x, y, bar[0], bar[1], bar[2]);
      }
      // The same letter as on their chests, cut small.
      const x0 = 6;
      const y0 = 6;
      for (let i = 0; i < 5; i++) {
        draw(x0 + i, y0, glow[0], glow[1], glow[2]);
        draw(x0 + i, y0 + 4, glow[0], glow[1], glow[2]);
        draw(x0 + (4 - i), y0 + i, glow[0], glow[1], glow[2]);
      }
      break;
    }
    case "wallEdge": {
      // A shadow down one side and along the foot: enough to say "this is a
      // face of something thin" without patterning the whitewash.
      const shade = line;
      for (let y = 0; y < TILE_PX; y++) {
        draw(0, y, shade[0], shade[1], shade[2]);
        draw(15, y, shade[0] * 0.94, shade[1] * 0.94, shade[2] * 0.94);
      }
      for (let x = 0; x < TILE_PX; x++) {
        draw(x, 15, shade[0] * 0.9, shade[1] * 0.9, shade[2] * 0.9);
      }
      break;
    }
    case "stairIcon": {
      // A step seen from the side: the lower tread runs the full width, the
      // upper half stands on the right, and the edges that face up or
      // forward are drawn darker so the two treads read as two.
      const face: [number, number, number] = spec.base;
      for (let y = 0; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const lower = y >= 8;
          const upper = y < 8 && x >= 8;
          if (!lower && !upper) continue;
          const edge = (upper && (y === 0 || x === 8)) || (lower && y === 8 && x < 8);
          const c = edge ? line : face;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "slabIcon": {
      // The material in the bottom half, a darker line along its top edge,
      // and nothing above it: a half block seen from the side.
      const face: [number, number, number] = spec.base;
      for (let y = 8; y < 16; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const c = y === 8 ? line : face;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "ingot": {
      // A bar seen from above: a trapezoid, bright along the top edge and
      // dark along the bottom, which is enough to read as cast metal.
      const metal = line;
      const lit: [number, number, number] = [
        Math.min(255, metal[0] * 1.3 + 30), Math.min(255, metal[1] * 1.3 + 30),
        Math.min(255, metal[2] * 1.3 + 30),
      ];
      const dark: [number, number, number] =
        [metal[0] * 0.55, metal[1] * 0.55, metal[2] * 0.55];
      const rows: readonly (readonly [number, number, number])[] = [
        [6, 5, 10], [7, 4, 11], [8, 3, 12], [9, 3, 12], [10, 4, 11],
      ];
      for (const [y, x0, x1] of rows) {
        for (let x = x0; x <= x1; x++) {
          const c = y === 6 ? lit : y >= 9 ? dark : metal;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "tank": {
      /*
       * Side on, because that is the only view of a tank that is a tank.
       *
       * Four things, in the order they read: a track that runs the length of
       * it, a hull sitting on it, a turret on top of that, and a gun out of
       * the front. Drop any one of them and it is a lorry.
       */
      const hull: [number, number, number] = [74, 88, 62];
      const hullLit: [number, number, number] = [98, 114, 82];
      const dark: [number, number, number] = [44, 52, 38];
      const track: [number, number, number] = [42, 40, 40];
      const wheel: [number, number, number] = [70, 68, 66];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put3(x, y, c);
      };

      // Tracks, with road wheels showing through them.
      span(13, 1, 14, track);
      span(12, 1, 14, track);
      for (let x = 2; x <= 13; x += 3) put3(x, 12, wheel);
      span(14, 2, 13, dark);

      // Hull: a slab with a sloped front, which is the other half of the
      // silhouette - a rectangle on tracks is a wagon.
      span(11, 1, 14, hull);
      span(10, 2, 14, hull);
      span(9, 4, 14, hull);
      put3(3, 10, dark); put3(2, 11, dark);

      // Turret, back of centre, and the gun out of the front of it.
      span(8, 6, 11, hull);
      span(7, 7, 10, hullLit);
      span(6, 8, 10, dark);
      span(7, 1, 6, dark);
      put3(1, 7, hullLit);
      break;
    }
    case "powder": {
      /*
       * A heap of meal, not a bag of it.
       *
       * Порох at sixteen pixels has to be *grains* - a flat grey mound reads
       * as stone - so the heap is drawn as a pile with a lit crown and then
       * speckled dark and light over the whole of it.
       */
      const dark: [number, number, number] = [38, 36, 38];
      const mid: [number, number, number] = [72, 68, 70];
      const lit: [number, number, number] = [116, 112, 112];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 7; y < 15; y++) {
        // A mound: widest at the foot, rounded over the top.
        const half = Math.round(Math.sqrt(Math.max(0, (y - 5) * 7)));
        for (let x = 8 - half; x <= 7 + half; x++) {
          const n = noise(x, y, tile + 71);
          put3(x, y, n > 0.72 ? lit : n > 0.34 ? mid : dark);
        }
      }
      // And a few grains that have rolled off it.
      for (const [gx, gy] of [[3, 14], [12, 14], [5, 13], [11, 12]] as const) {
        put3(gx, gy, mid);
      }
      break;
    }
    case "rubber": {
      /*
       * A slab of it: black, matte, with one soft highlight.
       *
       * The trap here is drawing something so dark it reads as a hole in the
       * tile sheet, which is what the first незламний блок did. One band of
       * grey across the upper third fixes it: a sheen says "surface".
       */
      const black: [number, number, number] = [26, 26, 28];
      const grey: [number, number, number] = [58, 58, 62];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 3; y <= 13; y++) {
        for (let x = 2; x <= 13; x++) {
          const n = noise(x, y, tile + 211);
          put3(x, y, n > 0.82 ? grey : black);
        }
      }
      for (let x = 4; x <= 11; x++) put3(x, 5, grey);
      break;
    }
    case "shell": {
      /*
       * A brass case with a steel head, standing on its base.
       *
       * Two materials and a shoulder between them, which is what tells a
       * shell from a bullet from a bottle at this size.
       */
      const brass: [number, number, number] = [176, 142, 58];
      const brassLit: [number, number, number] = [214, 182, 96];
      const steel: [number, number, number] = [150, 152, 160];
      const steelLit: [number, number, number] = [192, 194, 202];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      // The case: five wide, from the base up to the shoulder.
      for (let y = 8; y <= 14; y++) {
        for (let x = 6; x <= 10; x++) put3(x, y, x === 7 ? brassLit : brass);
      }
      // The shoulder, and then the head tapering to a point.
      for (let x = 6; x <= 10; x++) put3(x, 7, steel);
      for (let x = 6; x <= 10; x++) put3(x, 6, x === 7 ? steelLit : steel);
      for (let x = 7; x <= 9; x++) put3(x, 5, x === 7 ? steelLit : steel);
      for (let x = 7; x <= 9; x++) put3(x, 4, steel);
      put3(8, 3, steelLit);
      // A rim at the foot, so it stands rather than floats.
      for (let x = 5; x <= 11; x++) put3(x, 15, [128, 100, 40]);
      break;
    }
    case "flagUA": {
      /*
       * Синьо-жовтий, with a pole down the left and a fly to the right.
       *
       * Drawn as cloth rather than as two stripes filling the tile: the
       * block is rendered as two crossed quads, so what is not painted is
       * not there, and a flag has an edge, a pole and air around it.
       */
      const blue: [number, number, number] = [0, 91, 187];
      const yellow: [number, number, number] = [255, 213, 0];
      const pole: [number, number, number] = [96, 70, 42];
      const poleLit: [number, number, number] = [132, 98, 58];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 0; y < TILE_PX; y++) {
        put3(2, y, pole);
        put3(3, y, poleLit);
      }
      for (let y = 2; y <= 12; y++) {
        // A shallow wave along the fly, so it hangs rather than hovers.
        const wave = Math.round(Math.sin(y * 0.5) * 0.8);
        for (let x = 4; x <= 14 + wave; x++) {
          put3(x, y, y <= 7 ? blue : yellow);
        }
      }
      break;
    }
    case "flagBlack": {
      /*
       * Чорний прапор: the bots', and unmistakably not anybody's country.
       *
       * A skull rather than a plain black sheet, because at a distance a
       * black flag and a flag in shadow are the same thing.
       */
      const cloth: [number, number, number] = [26, 26, 30];
      const bone: [number, number, number] = [226, 224, 214];
      const pole: [number, number, number] = [96, 70, 42];
      const poleLit: [number, number, number] = [132, 98, 58];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 0; y < TILE_PX; y++) {
        put3(2, y, pole);
        put3(3, y, poleLit);
      }
      for (let y = 2; y <= 12; y++) {
        const wave = Math.round(Math.sin(y * 0.5) * 0.8);
        for (let x = 4; x <= 14 + wave; x++) put3(x, y, cloth);
      }
      /*
       * The skull: a crown narrower than the cheekbones, then a jaw.
       *
       * The first draft was a four-by-four square with two eyes, which at
       * this size reads as a face on a machine rather than as a skull. What
       * makes it bone is the taper - wide at the temples, narrow at the
       * chin - and eyes with a nose between them.
       */
      for (let x = 9; x <= 11; x++) put3(x, 4, bone);
      for (let y = 5; y <= 6; y++) for (let x = 8; x <= 12; x++) put3(x, y, bone);
      for (let x = 9; x <= 11; x++) put3(x, 7, bone);
      for (let x = 9; x <= 11; x++) put3(x, 8, bone);
      put3(9, 6, cloth); put3(11, 6, cloth);   // eyes
      put3(10, 7, cloth);                      // nose
      put3(9, 8, cloth); put3(11, 8, cloth);   // teeth, by what is between them
      // And the bone under it, knobbed at both ends the way one is drawn.
      for (let x = 8; x <= 12; x++) put3(x, 10, bone);
      put3(7, 9, bone); put3(7, 11, bone);
      put3(13, 9, bone); put3(13, 11, bone);
      break;
    }
    case "crystal": {
      /*
       * Адамантій: shards in the rock, not blobs of it.
       *
       * The ore pattern beside this one draws plus-signs, which is right for
       * a mineral you smelt - coal and iron come out of the ground as lumps.
       * A crystal does not: it has flat faces and a long axis, and at
       * sixteen pixels that has to be a *diamond with a highlight on one
       * side*, or it reads as another lump in another colour.
       */
      const ice = line;
      const iceLit: [number, number, number] = [
        Math.min(255, ice[0] * 1.35 + 40),
        Math.min(255, ice[1] * 1.35 + 40),
        Math.min(255, ice[2] * 1.3 + 30),
      ];
      const iceDark: [number, number, number] = [
        Math.round(ice[0] * 0.55), Math.round(ice[1] * 0.6),
        Math.round(ice[2] * 0.72),
      ];
      const at = (x: number, y: number, c: [number, number, number]): void =>
        draw((x + TILE_PX) % TILE_PX, (y + TILE_PX) % TILE_PX, c[0], c[1], c[2]);
      for (let cy = 3; cy < TILE_PX; cy += 6) {
        for (let cx = 3; cx < TILE_PX; cx += 6) {
          const n = noise(cx, cy, tile + 613);
          if (n < 0.34) continue;
          const x = cx + Math.floor(noise(cx, cy, tile + 77) * 3);
          const y = cy + Math.floor(noise(cx, cy, tile + 131) * 3);
          const tall = n > 0.66 ? 2 : 1;
          // The body: a diamond, two tall where there is room for it.
          for (let dy = -tall; dy <= tall; dy++) {
            const half = tall - Math.abs(dy);
            for (let dx = -half; dx <= half; dx++) at(x + dx, y + dy, ice);
          }
          // One lit face and one shadowed, so it has an edge rather than a
          // circumference.
          at(x, y - tall, iceLit);
          at(x - 1, y, iceLit);
          at(x + 1, y, iceDark);
          at(x, y + tall, iceDark);
        }
      }
      break;
    }
    case "ore": {
      // Blobs of mineral in the host rock, placed from the tile's own noise so
      // every ore is laid out differently and none of them tile into a grid.
      // Each blob is a plus sign with its corners knocked off - at 16 px a
      // circle and a square are the same four pixels, and a plus reads as
      // "lump" where a square reads as "brick".
      const ore = line;
      const oreLit: [number, number, number] = [
        Math.min(255, ore[0] * 1.5 + 30),
        Math.min(255, ore[1] * 1.5 + 30),
        Math.min(255, ore[2] * 1.5 + 30),
      ];
      for (let cy = 2; cy < TILE_PX; cy += 5) {
        for (let cx = 2; cx < TILE_PX; cx += 5) {
          const n = noise(cx, cy, tile + 613);
          if (n < 0.42) continue;
          const x = (cx + Math.floor(noise(cx, cy, tile + 77) * 3)) % TILE_PX;
          const y = (cy + Math.floor(noise(cx, cy, tile + 131) * 3)) % TILE_PX;
          const big = n > 0.72;
          const at = (dx: number, dy: number, c: [number, number, number]): void =>
            draw((x + dx + TILE_PX) % TILE_PX, (y + dy + TILE_PX) % TILE_PX,
              c[0], c[1], c[2]);
          at(0, 0, oreLit);
          at(1, 0, ore); at(-1, 0, ore); at(0, 1, ore); at(0, -1, ore);
          if (big) { at(1, 1, ore); at(-1, 1, ore); at(1, -1, ore); at(-1, -1, ore); }
        }
      }
      break;
    }
    case "lump": {
      // What you carry away: one nugget, lit from the top left like every
      // other item icon here.
      const ore = line;
      const lit: [number, number, number] = [
        Math.min(255, ore[0] * 1.45 + 26),
        Math.min(255, ore[1] * 1.45 + 26),
        Math.min(255, ore[2] * 1.45 + 26),
      ];
      const dark: [number, number, number] = [ore[0] * 0.6, ore[1] * 0.6, ore[2] * 0.6];
      // Rows of [y, from, to]. Written out rather than computed: the first
      // version had the y values wandering up and down and drew some rows
      // twice and others not at all.
      const rows: readonly (readonly [number, number, number])[] = [
        [4, 6, 9], [5, 5, 10], [6, 4, 11], [7, 4, 11],
        [8, 4, 11], [9, 5, 10], [10, 6, 9],
      ];
      for (const [y, x0, x1] of rows) {
        for (let x = x0; x <= x1; x++) {
          const c = (y <= 5 || x === x0) ? lit : (y >= 9 || x === x1) ? dark : ore;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "flint": {
      /*
       * Кресало: a curved steel striker and the flint it is struck against.
       *
       * The steel is the shape - a bent bar with a loop for two fingers -
       * and the flint is the grey lump beside it. Two objects that only mean
       * anything together, which is why the tile draws both.
       */
      const steel: [number, number, number] = [176, 178, 186];
      const shade: [number, number, number] = [118, 120, 128];
      const flint: [number, number, number] = [92, 92, 96];
      const spark: [number, number, number] = [250, 214, 120];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      // The striker: a C lying on its back.
      for (let x = 4; x <= 11; x++) { put(x, 5, steel); put(x, 6, shade); }
      put(3, 6, steel); put(3, 7, steel); put(4, 8, steel);
      put(12, 6, steel); put(12, 7, steel); put(11, 8, steel);
      put(5, 8, shade); put(10, 8, shade);
      // The flint, under it.
      for (let y = 10; y <= 12; y++) for (let x = 5; x <= 10; x++) put(x, y, flint);
      put(4, 11, flint); put(11, 11, flint);
      put(5, 10, shade); put(10, 12, shade);
      // And the spark between them, which is the whole point of the pair.
      put(8, 9, spark); put(9, 9, spark); put(9, 8, spark);
      break;
    }
    case "boat": {
      /*
       * A човен seen from above and a little to one side.
       *
       * From straight above it is a rectangle, and a rectangle is a plank.
       * So the tile is drawn as a shallow trough at an angle: the near
       * gunwale low across the middle, the far one behind it, and the dark
       * of the inside between them. The two ends rise, which is the one
       * feature that says boat rather than box.
       */
      const hull: [number, number, number] = [154, 111, 66];
      const lip: [number, number, number] = [186, 139, 86];
      const dark: [number, number, number] = [92, 64, 38];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      // The far side, and the inside under it.
      for (let x = 3; x <= 12; x++) put(x, 6, lip);
      for (let x = 3; x <= 12; x++) { put(x, 7, dark); put(x, 8, dark); }
      // The near side.
      for (let x = 3; x <= 12; x++) { put(x, 9, hull); put(x, 10, hull); }
      // The two ends, standing proud of both.
      for (let y = 4; y <= 10; y++) { put(2, y, hull); put(13, y, hull); }
      put(2, 3, lip); put(13, 3, lip);
      put(1, 5, hull); put(14, 5, hull);
      break;
    }
    case "bone": {
      /*
       * Кістка: a shaft with a knuckle at each end.
       *
       * Drawn on the diagonal, because a bone lying straight up and down at
       * sixteen pixels is a stick. The knuckles are two pixels wider than
       * the shaft and that is the whole of what makes it read as bone.
       */
      const bone: [number, number, number] = [235, 230, 214];
      const shade: [number, number, number] = [186, 180, 162];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let i = 0; i < 9; i++) {
        put(4 + i, 11 - i, bone);
        put(5 + i, 11 - i, bone);
        put(4 + i, 12 - i, shade);
      }
      // The four knuckles, two at each end.
      for (const [kx, ky] of [[2, 11], [2, 13], [12, 1], [12, 3]] as const) {
        put(kx, ky, bone); put(kx + 1, ky, bone);
        put(kx, ky + 1, shade); put(kx + 1, ky + 1, shade);
      }
      break;
    }
    case "pelt": {
      /*
       * Шкура вовкулаки: the whole animal, flattened.
       *
       * Three earlier tries are why it looks like this. A rectangle pegged at
       * four corners read as a scroll on a rack; a dark spine with legs off
       * the sides read as a beetle; paws drawn clear of the body read as a
       * beetle's legs. What fixed it was making the legs part of the one
       * outline and dropping the stripe for a speckle - a pelt is a broad
       * flat shape with a head on it, and nothing down the middle. Grey
       * rather than leather-brown: that is the coat it comes off.
       */
      const fur: [number, number, number] = [118, 114, 106];
      const speck: [number, number, number] = [98, 94, 88];
      const edge: [number, number, number] = [156, 152, 144];
      const ear: [number, number, number] = [70, 68, 66];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      // One silhouette: head, body, and the legs spreading out of it.
      const span = (y: number): [number, number] | null =>
        y <= 3 ? [5, 10] :
        y === 5 || y === 6 || y === 11 || y === 12 ? [1, 14] :
        y <= 13 ? [3, 12] : null;
      for (let y = 0; y <= 13; y++) {
        const reach = span(y);
        if (!reach) continue;
        const [x0, x1] = reach;
        for (let x = x0; x <= x1; x++) {
          if (x === x0 || x === x1 || y === 0 || y === 13) put(x, y, edge);
          else put(x, y, (x * 3 + y * 5) % 7 === 0 ? speck : fur);
        }
      }
      // Two ears, so which end is the head is never in doubt.
      put(4, 0, ear); put(11, 0, ear);
      // And the tail, off the far end.
      put(7, 14, fur); put(8, 14, speck);
      break;
    }
    case "wattle": {
      /*
       * Плетений тин: willow woven through standing posts.
       *
       * From the photograph, and the photograph is the whole point - a тин
       * is not a plank fence and it is not pickets either. It is живопліт
       * work: stakes driven into the ground and green willow rods bent
       * through them, one in front, the next behind, so the weave holds
       * itself together with nothing but the bend in the wood.
       *
       * Two posts to a block, brown; five courses of withy between them,
       * the grey-green of willow bark. Which post a course passes in front
       * of alternates by course, and that alternation *is* the weave: draw
       * the posts, draw the rod over them, then put back the one post the
       * rod goes behind.
       */
      const bark: [number, number, number] = [126, 132, 100];
      const barkLit: [number, number, number] = [158, 164, 126];
      const barkDark: [number, number, number] = [84, 90, 66];
      const post: [number, number, number] = [106, 78, 52];
      const postLit: [number, number, number] = [132, 100, 68];
      const postDark: [number, number, number] = [70, 52, 34];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const POSTS = [2, 10];
      const stake = (x0: number): void => {
        for (let y = 0; y < 16; y++) {
          put3(x0, y, postLit);
          put3(x0 + 1, y, post);
          put3(x0 + 2, y, postDark);
        }
      };
      for (const x0 of POSTS) stake(x0);
      // Five courses, three pixels apart: two of rod and a dark line where
      // the next one laps over it.
      for (let course = 0; course < 6; course++) {
        const y = course * 3;
        if (y > 15) break;
        for (let x = 0; x < 16; x++) {
          // The rod bows between the stakes rather than running dead level.
          const bow = ((x + course * 5) % 8) < 4 ? 0 : 1;
          const top = Math.min(15, y + bow);
          put3(x, top, barkLit);
          if (top + 1 <= 15) put3(x, top + 1, bark);
          if (top + 2 <= 15) put3(x, top + 2, barkDark);
        }
        // And the stake this course passes behind goes back over it.
        const behind = POSTS[course % 2]!;
        for (let y2 = y; y2 <= Math.min(15, y + 2); y2++) {
          put3(behind, y2, postLit);
          put3(behind + 1, y2, post);
          put3(behind + 2, y2, postDark);
        }
      }
      break;
    }
    case "wattleTop": {
      /*
       * The same fence from above: cut ends.
       *
       * Two stake tops, and the withies between them seen end-on. Looking
       * down on a тин is what you do while building one, and the side tile
       * turned on its edge reads as a woven floor.
       */
      const bark: [number, number, number] = [126, 132, 100];
      const barkDark: [number, number, number] = [84, 90, 66];
      const post: [number, number, number] = [106, 78, 52];
      const postLit: [number, number, number] = [140, 108, 74];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      /*
       * Rods all the way across, not a stripe down the middle.
       *
       * The first version put a four-pixel band of withy in a field of
       * shadow, and the top of a тин post is half a block square - the tile
       * is squeezed into it, and a band became a thread. Looking down, what
       * is actually there is course after course of rod, so that is what
       * this is: a line each, light and dark alternating.
       */
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) {
          put3(x, y, y % 3 === 2 ? barkDark : bark);
        }
      }
      // The two stake tops, sawn off flat and paler for it.
      for (const x0 of [2, 10]) {
        for (let y = 5; y <= 10; y++) {
          for (let x = x0; x <= x0 + 2; x++) {
            put3(x, y, y === 5 || y === 10 || x === x0 ? post : postLit);
          }
        }
      }
      break;
    }
    case "shield": {
      /*
       * Щит: boards down, an iron rim round, a cross across.
       *
       * Drawn as a *sprite* - this is an item tile, so everything outside
       * the shield has to be transparent or it is a square plank with a
       * picture on it. The shape is the long kite of the drawing narrowed to a
       * plank at the foot, which at sixteen pixels is three widths: broad at
       * the shoulder, square in the middle, tapered at the bottom.
       */
      const board: [number, number, number] = [128, 94, 58];
      const dark: [number, number, number] = [96, 68, 40];
      const iron: [number, number, number] = [158, 164, 172];
      const shade: [number, number, number] = [92, 98, 106];
      const half = (y: number): number =>
        y < 2 ? 0 : y < 4 ? 4 : y < 11 ? 5 : y < 14 ? 4 : 3;
      for (let y = 0; y < 16; y++) {
        const reach = half(y);
        if (reach === 0) continue;
        for (let x = 8 - reach; x < 8 + reach; x++) {
          /*
           * One field of boards, and no seams drawn between them.
           *
           * Seams every third column made a lattice with the rim and the
           * cross; every fourth made a smaller one. The arithmetic is
           * against it - ten columns of shield, a bar across the middle, and
           * any vertical line at all cuts what is left into squares. A
           * shield at this size is a shape with a cross on it, and the
           * boards are told by the grain of the wood rather than by lines.
           */
          let c = board;
          // The rim, all the way round.
          const edge = x === 8 - reach || x === 8 + reach - 1 ||
            half(y - 1) < reach || half(y + 1) < reach;
          if (edge) c = iron;
          // A little grain, so the field is wood and not paint.
          if ((x * 3 + y * 5) % 11 === 0) c = dark;
          // And the cross: the one thing that says whose it is.
          // One pixel thick, not two. A cross four pixels wide on a
          // ten-wide shield is most of the shield.
          const arm = (x === 8 && y >= 3 && y <= 12) ||
            (y === 7 && x >= 5 && x <= 10);
          // The boss where the arms meet, a shade down so the cross has a
          // middle rather than being two bars crossing in thin air.
          if (arm && !edge) c = (x === 8 && y === 7) ? shade : iron;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "whitewash": {
      /*
       * Limewash over clay: not flat white, and not brick.
       *
       * A wall like this is hand-smoothed and painted over every year, so
       * what it has instead of a pattern is *unevenness* - faint hollows
       * where the daub sits over the weave underneath, and a warmer line
       * along the bottom where the rain splashes the footing. Flat white
       * read as porcelain beside the straw, which is the one thing a village
       * must not look like.
       */
      const lime: [number, number, number] = [226, 222, 210];
      const shade: [number, number, number] = [206, 200, 186];
      const warm: [number, number, number] = [196, 184, 160];
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) {
          // Broad soft hollows rather than speckle: two low-frequency waves
          // crossed, which at sixteen pixels reads as a hand-smoothed face.
          const wave = Math.sin((x + 1) * 0.7) * Math.cos((y + 2) * 0.55);
          let c = wave < -0.35 ? shade : lime;
          // The splash line along the foot of the wall.
          if (y >= 14) c = warm;
          const n = noise(x, y, tile);
          const lift = n < 0.12 ? 6 : n > 0.9 ? -5 : 0;
          draw(x, y, c[0] + lift, c[1] + lift, c[2] + lift);
        }
      }
      break;
    }
    case "cursed": {
      /*
       * Проклятий камінь: the rock the замок is cut out of.
       *
       * Dark slate rather than the grey stone the rest of the world is made
       * of, so a doorway in a mountainside reads as *cut* rather than as a
       * cave. The blue in it is the same blue as the fire Чахлик throws -
       * one colour shared between the thing and the place, which is what
       * makes a lair look like it belongs to somebody.
       *
       * Veins rather than speckle: slate splits along lines, and lines at
       * this size are the one thing that does not read as noise.
       */
      /*
       * Dark, but not a hole in the screen.
       *
       * These were a third the brightness of the game's own stone ([126,
       * 126, 130]) and it did not survive contact with the world: a face
       * gets shaded by which way it points before it is drawn, and a wall of
       * this size at a third of grey came out as flat black. Photographed
       * from inside the game it read as a void with a texture rather than as
       * rock - "there is no castle" - so it is lifted to about two thirds of
       * stone, which is still plainly the darkest thing in the world and is
       * now plainly *stone*.
       */
      const rock: [number, number, number] = [82, 86, 100];
      const dark: [number, number, number] = [58, 61, 73];
      const pale: [number, number, number] = [104, 109, 126];
      const vein: [number, number, number] = [76, 124, 172];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) {
          const n = (x * 7 + y * 13) % 11;
          put3(x, y, n === 0 ? pale : n === 1 || n === 5 ? dark : rock);
        }
      }
      // Two seams of cold blue, running the way a split runs.
      for (let i = 0; i < 16; i++) {
        put3(i, (3 + Math.floor(i / 3)) % 16, vein);
        put3((i + 8) % 16, (11 + Math.floor(i / 5)) % 16, vein);
      }
      break;
    }
    case "cursedBrick": {
      /*
       * The dressed version: what the walls inside are built of.
       *
       * Courses offset by half a brick, which is how a wall is laid and how
       * it reads as *built* beside the rough stone it is cut into. Same
       * palette, so a room and the rock around it are plainly one place.
       */
      const brick: [number, number, number] = [94, 99, 114];
      const mortar: [number, number, number] = [56, 59, 71];
      const lit: [number, number, number] = [118, 124, 142];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) put3(x, y, brick);
      }
      for (let course = 0; course < 4; course++) {
        const y = course * 4;
        for (let x = 0; x < 16; x++) put3(x, y, mortar);
        // The upright joint, half a brick along on every other course.
        const shift = course % 2 === 0 ? 0 : 8;
        for (let d = 1; d < 4; d++) {
          put3((shift + 8) % 16, y + d, mortar);
          put3(shift % 16, y + d, mortar);
        }
        // A highlight under each course, so the wall has depth at a glance.
        for (let x = 0; x < 16; x++) {
          if ((x + course) % 5 !== 0) put3(x, y + 1, lit);
        }
      }
      break;
    }
    case "cursedLamp": {
      /*
       * Синій ліхтар: the only light in the замок, and it is the wrong colour.
       *
       * Cold blue, like the fire. A castle lit by ordinary torches is a
       * castle somebody lives in; this one is lit by whatever he is.
       */
      const iron: [number, number, number] = [38, 40, 48];
      const glass: [number, number, number] = [70, 132, 190];
      const core: [number, number, number] = [186, 230, 255];
      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) {
          const edge = x < 3 || x > 12 || y < 2 || y > 13;
          put3(x, y, edge ? iron : glass);
        }
      }
      // The flame inside it, brightest in the middle.
      for (let y = 5; y <= 10; y++) {
        for (let x = 6; x <= 9; x++) {
          put3(x, y, (x === 7 || x === 8) && y > 5 && y < 10 ? core : glass);
        }
      }
      // And the strap over the top, so it hangs rather than floats.
      for (let x = 6; x <= 9; x++) put3(x, 0, iron);
      break;
    }
    case "ice": {
      /*
       * Cracks, and a sheen where the light comes off it.
       *
       * Straight segments running edge to edge, so they carry on into the
       * neighbouring block and a frozen lake reads as one sheet rather than
       * as a grid of tiles. Every ice block in the world uses this one tile,
       * so a crack leaving the right edge meets its own self coming in at
       * the left - which is why they are drawn at the mid-points, where the
       * two ends line up.
       */
      const crack = (
        x0: number, y0: number, x1: number, y1: number,
      ): void => {
        const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          set(Math.round(x0 + (x1 - x0) * t), Math.round(y0 + (y1 - y0) * t), 0.85);
        }
      };
      // Three of them, meeting near the middle the way a struck sheet goes.
      crack(0, 5, 8, 8);
      crack(8, 8, 15, 3);
      crack(8, 8, 11, 15);
      // A paler second set, half strength: the older cracks under the new.
      const faint = (
        x0: number, y0: number, x1: number, y1: number,
      ): void => {
        const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          set(Math.round(x0 + (x1 - x0) * t), Math.round(y0 + (y1 - y0) * t), 0.3);
        }
      };
      faint(0, 12, 6, 15);
      faint(12, 0, 15, 6);
      // And the sheen: a bright patch where a flat frozen thing catches the
      // sky. Without it the tile is a pale grey square with scratches on it.
      for (let y = 1; y < 5; y++) {
        for (let x = 2; x < 7 - y; x++) draw(x, y, 244, 251, 255);
      }
      break;
    }
    case "current": {
      // Streaks drawn along one axis, so that scrolling the texture reads as
      // movement rather than as static noise sliding about. They have to wrap
      // top to bottom: the shader offsets v continuously, and a seam would
      // travel through the water once a second.
      const streak = line;
      for (let y = 0; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          // A diagonal wave whose period divides the tile in *both* axes, so
          // the streaks join up across every edge. This matters more here than
          // for any other tile: the shader scrolls this one, so a break at the
          // edge is not a static blemish but a line dragged through the water
          // once per cycle. An arbitrary multiplier on x - it was 0.9 - looks
          // fine on a single tile and breaks every sixteen pixels across a lake.
          //
          // Checked by rendering it tiled and looking, not by a test. I wrote
          // one comparing the difference across the wrap against the
          // differences inside the tile, and it scored the broken version
          // *better* than this one: a smooth wave already differs plenty from
          // column to column, which drowns the discontinuity. It is deleted
          // rather than kept as decoration.
          const wave = Math.sin((2 * Math.PI * (y + 2 * x)) / TILE_PX);
          const n = noise(x, y, tile + 41);
          if (wave < 0.45 || n < 0.35) continue;
          const lit = 0.55 + 0.45 * wave;
          draw(x, y, streak[0] * lit, streak[1] * lit, streak[2] * lit);
        }
      }
      break;
    }
    case "hemp": {
      // Nothing else in the game has this silhouette, and that is the point:
      // a stand of hemp should be readable across the steppe.
      //
      // Five separate leaflets, each a lens shape measured off its own axis,
      // rather than one filled fan. At 16 px the gaps between the leaflets
      // are what makes the leaf legible - a fan with the gaps closed reads as
      // a bush, which is what the first two attempts at this looked like.
      // Darker than the steppe it grows on. A mid-green plant on mid-green
      // grass is invisible from ten blocks away, however good the shape is.
      const stalk: [number, number, number] = [72, 108, 48];
      const leaf: [number, number, number] = [44, 88, 38];
      const leafLit: [number, number, number] = [70, 122, 50];
      const vein: [number, number, number] = [104, 156, 70];

      const cx = 8;
      const cy = 12;
      /** Angle from vertical, length, and half-width of each leaflet. */
      const LEAFLETS: readonly (readonly [number, number, number])[] = [
        [-1.24, 7.0, 1.25],
        [-0.70, 9.0, 1.20],
        [0, 10.5, 1.25],
        [0.70, 9.0, 1.20],
        [1.24, 7.0, 1.25],
      ];

      for (let y = cy; y < TILE_PX; y++) draw(cx, y, stalk[0], stalk[1], stalk[2]);

      for (const [angle, len, halfWidth] of LEAFLETS) {
        // Up the tile is -y, so the axis points up and out from the node.
        const ux = Math.sin(angle);
        const uy = -Math.cos(angle);
        for (let y = 0; y < TILE_PX; y++) {
          for (let x = 0; x < TILE_PX; x++) {
            const dx = x - cx;
            const dy = y - cy;
            const along = dx * ux + dy * uy;
            if (along < 0.4 || along > len) continue;
            const across = Math.abs(dx * uy - dy * ux);
            // Widest at two fifths of the way out, tapering to a point at the
            // tip and back to the stem - the shape of a real leaflet.
            const w = halfWidth * Math.sin(Math.PI * (along / len) ** 0.8);
            if (across > w) continue;
            // A short spine, not a full-length stripe: a vein drawn the
            // whole way out reads as a stick with leaves stuck to it.
            const c = across < 0.5 && along > 2 && along < len - 2
              ? vein
              : across < 0.85 ? leafLit : leaf;
            draw(x, y, c[0], c[1], c[2]);
          }
        }
      }
      break;
    }
    case "stalks": {
      // A bundle of cut stalks, tied. Still woody and grey-green.
      const stalk = line;
      const tie: [number, number, number] = [140, 104, 62];
      for (let k = 0; k < 6; k++) {
        const x = 3 + k * 2;
        for (let y = 2; y < 14; y++) {
          const shade = k % 2 === 0 ? 1 : 0.82;
          draw(x, y, stalk[0] * shade, stalk[1] * shade, stalk[2] * shade);
        }
      }
      for (let x = 2; x < 14; x++) {
        draw(x, 7, tie[0], tie[1], tie[2]);
        draw(x, 8, tie[0] * 0.75, tie[1] * 0.75, tie[2] * 0.75);
      }
      break;
    }
    case "fibre": {
      // Retted fibre: soft, pale, combed out into a hank rather than a bundle.
      const f = line;
      for (let k = 0; k < 9; k++) {
        const x = 3 + k;
        const top = 3 + Math.floor(noise(k, 0, tile + 173) * 3);
        const bot = 13 - Math.floor(noise(k, 1, tile + 179) * 3);
        for (let y = top; y <= bot; y++) {
          const shade = 0.82 + noise(k, y, tile + 181) * 0.3;
          draw(x, y, Math.min(255, f[0] * shade),
            Math.min(255, f[1] * shade), Math.min(255, f[2] * shade));
        }
      }
      break;
    }
    case "linen": {
      // A folded length of cloth: pale, with the weave showing and one
      // stitched band, so it reads as the same textile the bed is dressed in.
      const cloth: [number, number, number] = [236, 230, 214];
      const fold: [number, number, number] = [206, 199, 182];
      for (let y = 3; y < 13; y++) {
        for (let x = 2; x < 14; x++) {
          const weave = (x + y) % 3 === 0 ? 0.94 : 1;
          draw(x, y, cloth[0] * weave, cloth[1] * weave, cloth[2] * weave);
        }
      }
      for (const fy of [6, 10]) {
        for (let x = 2; x < 14; x++) draw(x, fy, fold[0], fold[1], fold[2]);
      }
      stitchBand(draw, 8, STITCH_RED, STITCH_BLACK);
      break;
    }
    case "wheel": {
      // A spinning wheel: a spoked wheel on a frame, seen side on.
      const rim = line;
      const cx = 7;
      const cy = 7;
      for (let a = 0; a < 48; a++) {
        const t = (a / 48) * Math.PI * 2;
        const rx = Math.round(cx + Math.cos(t) * 5.5);
        const ry = Math.round(cy + Math.sin(t) * 5.5);
        set(rx, ry, 0.9);
      }
      // Four spokes and a hub.
      for (let k = -4; k <= 4; k++) {
        set(cx + k, cy, 0.7);
        set(cx, cy + k, 0.7);
      }
      set(cx, cy, 1);
      // The treadle bar along the foot.
      for (let x = 1; x < TILE_PX - 1; x++) set(x, TILE_PX - 2, 0.8);
      void rim;
      break;
    }
    case "rushnyk": {
      // An embroidered towel: bands of cross-stitch at both ends, plain linen
      // between, and a fringe along the bottom.
      stitchBand(draw, 3, STITCH_RED, STITCH_BLACK);
      stitchBand(draw, 12, STITCH_BLACK, STITCH_RED);
      for (let x = 0; x < TILE_PX; x += 2) set(x, TILE_PX - 1, 0.5);
      break;
    }
    case "furrows": {
      // Ridges left by the hoe: raised crests and shadowed troughs, four to a
      // block, wrapping so a whole field reads as one ploughed surface.
      for (let y = 0; y < TILE_PX; y++) {
        const phase = y % 4;
        if (phase === 0) {
          for (let x = 0; x < TILE_PX; x++) set(x, y, 0.75);
        } else if (phase === 1) {
          // A highlight on the near side of each ridge.
          for (let x = 0; x < TILE_PX; x++) {
            const i = (y * TILE_PX + x) * 4;
            out[i] = Math.min(255, out[i]! * 1.18);
            out[i + 1] = Math.min(255, out[i + 1]! * 1.18);
            out[i + 2] = Math.min(255, out[i + 2]! * 1.18);
          }
        }
      }
      break;
    }
    case "stick": {
      // A plain shaft running corner to corner.
      for (let k = 3; k < 13; k++) {
        draw(k, TILE_PX - 1 - k, line[0], line[1], line[2]);
        draw(k + 1, TILE_PX - 1 - k, line[0] * 0.78, line[1] * 0.78, line[2] * 0.78);
      }
      break;
    }
    case "hoe": {
      // The wooden hoe: a shaft with a head cut from a plank, lashed on. It
      // has to read as visibly cruder than the iron one - shorter, blunter,
      // paler, with the grain showing - because it is the tool you make before
      // you can make a better one.
      const head: [number, number, number] = [186, 140, 80];
      const headLit: [number, number, number] = [214, 168, 104];
      const headDark: [number, number, number] = [138, 100, 56];
      const grain: [number, number, number] = [156, 114, 62];
      const cord: [number, number, number] = [96, 78, 50];

      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put3(x, y, c);
      };

      drawShaft(draw);

      // Neck and binding. This has to reach the head - leaving row 10 empty
      // left the head floating a pixel clear of the shaft.
      put3(8, 8, headDark);
      put3(8, 9, cord);
      put3(9, 9, cord);
      put3(9, 10, cord);

      // Head: a thick blunt board, not a blade. Narrower than the iron head
      // and barely tapered, where iron runs 2 pixels wide to 12 - so the two
      // are told apart by silhouette as well as by colour.
      // The flat bottom edge is what makes either of these read as a hoe.
      // Tapering it away, as the first wooden attempt did, turns the head into
      // a lump. Iron runs 2 pixels wide to 12 over six rows; wood is blunter,
      // narrower and thicker - the same tool, worse made.
      span(11, 8, 10, headLit);
      span(12, 7, 12, head);
      span(13, 6, 13, head);
      span(14, 5, 14, head);
      span(15, 5, 14, headDark);

      // Grain as short horizontal dashes along the board, not scattered dots -
      // dots read as lumps, which is exactly how the first version looked.
      put3(8, 12, grain);
      put3(9, 12, grain);
      put3(11, 13, grain);
      put3(12, 13, grain);
      put3(7, 14, grain);
      break;
    }
    case "hoeIron": {
      // The iron hoe: the same shaft, a dark iron neck, and a plain dark
      // triangular head with a flat cutting edge and a lighter socket.
      const iron: [number, number, number] = [74, 74, 82];
      const ironLit: [number, number, number] = [108, 108, 118];
      const ironDark: [number, number, number] = [44, 44, 50];

      const put3 = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put3(x, y, c);
      };

      drawShaft(draw);

      put3(8, 8, iron);
      put3(8, 9, ironDark);
      put3(9, 9, iron);

      // Widening exactly one pixel each side per row is what makes it read as
      // a triangle rather than as a stack.
      span(10, 8, 9, iron);
      span(11, 7, 10, iron);
      span(12, 6, 11, iron);
      span(13, 5, 12, iron);
      span(14, 4, 13, iron);
      span(15, 3, 14, ironDark);

      // The eye the shaft sockets into, and nothing else - a highlight one
      // pixel inside the border reads as speckle at this size.
      put3(9, 11, ironLit);
      put3(9, 12, ironLit);
      break;
    }
    case "helm":
    case "mail":
    case "bracers":
    case "boots": {
      // Armour, all four in one place: the same three tones of iron and the
      // same two helpers, so a set looks like a set. What tells them apart at
      // 16 px is the outline and nothing else, which is why each is laid out
      // by hand rather than scaled from one shape.
      const iron: [number, number, number] = spec.line ?? [150, 152, 162];
      const lit = shade(iron, 1.28);
      const dark = shade(iron, 0.62);
      const leather: [number, number, number] = [116, 84, 50];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };

      if (spec.pattern === "helm") {
        // Шишак: a dome drawn to a point, with a nose bar. The spike is what
        // makes it Cossack rather than a bucket, so it gets three rows of its
        // own before the dome starts.
        put(8, 1, lit);
        put(8, 2, iron);
        put(8, 3, iron);
        span(4, 6, 10, lit);
        span(5, 5, 11, lit);
        span(6, 4, 12, iron);
        span(7, 4, 12, iron);
        span(8, 3, 13, iron);
        span(9, 3, 13, dark);   // the brow band
        span(10, 3, 13, dark);
        // Cheek pieces down each side and the nasal between them. Three
        // separate strips left gaps that read as legs under a mushroom, so
        // the cheeks are three pixels wide and the nasal joins the brow.
        // Cheeks four wide, not two. Narrow ones left the dome standing on
        // legs like a mushroom; what makes this a helmet is that the face
        // opening is a slot in a solid piece, not a gap between two posts.
        for (let y = 11; y <= 13; y++) {
          span(y, 3, 6, iron);
          put(3, y, dark);
          span(y, 10, 13, iron);
          put(13, y, dark);
        }
        span(14, 4, 5, dark);
        span(14, 11, 12, dark);
        // The nasal, hanging from the brow between them.
        for (let y = 11; y <= 14; y++) put(8, y, iron);
        put(8, 14, dark);
        // A left-hand highlight, so the dome reads as round.
        put(6, 6, lit);
        put(5, 7, lit);
      } else if (spec.pattern === "mail") {
        // Кольчуга: a shirt with short sleeves, and rings drawn as a checker.
        // A flat plate came out as a door; the checker is what says "mail".
        span(3, 5, 11, dark);        // collar
        put(7, 4, dark); put(8, 4, dark); put(9, 4, dark);
        for (let y = 4; y <= 13; y++) span(y, 5, 11, iron);
        span(4, 2, 4, iron);         // sleeves
        span(5, 2, 4, iron);
        span(6, 2, 3, iron);
        span(4, 12, 14, iron);
        span(5, 12, 14, iron);
        span(6, 13, 14, iron);
        // The rings. Every other pixel, offset row by row.
        for (let y = 5; y <= 13; y++) {
          for (let x = 5; x <= 11; x++) {
            if ((x + y) % 2 === 0) put(x, y, dark);
          }
        }
        span(4, 6, 10, lit);         // shoulders catch the light
        span(14, 5, 11, dark);       // hem
        put(8, 4, dark); put(8, 5, dark);  // the neck opening
      } else if (spec.pattern === "bracers") {
        // Наручі: a pair of cuffs, one behind the other, each strapped in
        // leather. Two of them rather than one, because a single cuff at this
        // size is indistinguishable from a ring.
        for (const x0 of [2, 9]) {
          span(4, x0, x0 + 4, lit);
          for (let y = 5; y <= 10; y++) span(y, x0, x0 + 4, iron);
          span(11, x0, x0 + 4, dark);
          // Straps across, which is what makes it worn rather than forged shut.
          span(6, x0, x0 + 4, leather);
          span(9, x0, x0 + 4, leather);
          put(x0, 5, dark);
          put(x0 + 4, 5, dark);
          put(x0 + 2, 7, lit);
        }
      } else {
        // Чоботи: a pair, seen from the side, toes pointing out.
        // The foot runs out one way only. Extending it evenly both ways came
        // out as two blocks standing on end - a boot is a boot because it
        // points somewhere.
        for (const [shin, dir] of [[3, -1], [10, 1]] as const) {
          for (let y = 3; y <= 10; y++) span(y, shin, shin + 2, iron);
          span(3, shin, shin + 2, lit);
          span(6, shin, shin + 2, leather);   // the strap over the instep
          const heel = dir < 0 ? shin + 2 : shin;
          const toe = dir < 0 ? shin - 2 : shin + 4;
          const x0 = Math.min(heel, toe);
          const x1 = Math.max(heel, toe);
          span(11, x0, x1, iron);
          span(12, x0, x1, iron);
          put(toe, 11, lit);
          span(13, x0, x1, dark);             // the sole
          put(heel, 13, dark);
        }
      }
      break;
    }
    case "tomato": {
      // Сила, one помідор at a time. Round, ribbed, with a green calyx and a
      // stub of stalk - what tells a tomato from an apple at 16 px is the
      // calyx, so it gets five points and they run over the shoulders of the
      // fruit rather than sitting on top of it.
      const skin: [number, number, number] = [198, 42, 38];
      const skinLit: [number, number, number] = [236, 88, 72];
      const skinDark: [number, number, number] = [142, 24, 26];
      const leaf: [number, number, number] = [78, 132, 52];
      const leafLit: [number, number, number] = [116, 172, 74];
      const stalk: [number, number, number] = [96, 116, 48];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };

      // The body: wider than it is tall, which is what a tomato is.
      span(4, 6, 9, skin);
      span(5, 4, 11, skin);
      span(6, 3, 12, skin);
      span(7, 2, 13, skin);
      span(8, 2, 13, skin);
      span(9, 2, 13, skin);
      span(10, 3, 12, skin);
      span(11, 3, 12, skin);
      span(12, 4, 11, skin);
      span(13, 6, 9, skin);

      // Shading: dark down the right and along the bottom, light up the left.
      for (const [x, y] of [[13, 7], [13, 8], [13, 9], [12, 10], [12, 11],
        [11, 12], [9, 13], [8, 13], [7, 13], [6, 13], [4, 12], [3, 11]] as const) {
        put(x, y, skinDark);
      }
      for (const [x, y] of [[4, 6], [3, 7], [2, 8], [3, 6], [5, 5],
        [4, 7], [3, 8]] as const) {
        put(x, y, skinLit);
      }
      // One bright spot where the light lands, and nothing more - two
      // highlights on a 16 px fruit read as mould.
      put(5, 7, [252, 158, 140]);
      put(4, 8, skinLit);

      // The calyx, over the shoulders.
      span(3, 7, 8, leaf);
      put(5, 4, leaf); put(6, 3, leafLit);
      put(10, 4, leaf); put(9, 3, leafLit);
      put(4, 5, leaf); put(11, 5, leaf);
      put(7, 4, leafLit); put(8, 4, leafLit);
      // The stalk.
      put(7, 1, stalk); put(8, 1, stalk);
      put(7, 2, stalk); put(8, 2, leafLit);
      break;
    }
    case "sapling": {
      // A seedling: a stem with a pair of leaves and a bud. Drawn against
      // nothing, because a cross-rendered block is a cut-out - every pixel not
      // painted here is see-through.
      //
      // The two species differ in silhouette as well as colour, the same way
      // the grown trees do: the дуб is round and low, the акація taller with
      // its leaves held out flat. Colour alone would be invisible against
      // grass at any distance.
      const pine = spec.species === "pine";
      const oak = spec.species !== "acacia" && !pine;
      const leaf: [number, number, number] =
        pine ? [46, 84, 60] : oak ? [86, 128, 52] : [126, 158, 74];
      const leafLit: [number, number, number] =
        pine ? [66, 112, 76] : oak ? [116, 164, 70] : [160, 190, 100];
      const leafDark: [number, number, number] =
        pine ? [30, 58, 44] : oak ? [58, 94, 38] : [92, 120, 54];
      const stem: [number, number, number] = [104, 82, 48];
      const stemDark: [number, number, number] = [74, 58, 34];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };

      // The stem, up the middle from the ground.
      const root = oak ? 9 : 7;
      for (let y = root; y <= 15; y++) put(7, y, y > 13 ? stemDark : stem);
      put(8, 15, stemDark);

      if (pine) {
        // A little fir: a spire of three tiers, narrowest at the top. Nothing
        // else in the game has that outline, which is the point - a sapling
        // has to say which tree it will be before it is one.
        put(7, 2, leafLit);
        span(3, 6, 8, leaf);
        span(4, 6, 8, leafDark);
        span(5, 5, 9, leaf);
        span(6, 5, 9, leafDark);
        span(7, 4, 10, leaf);
        span(8, 4, 10, leafDark);
        put(4, 7, leafLit); put(10, 7, leafDark);
      } else if (oak) {
        // Round and low: a small bushy head sitting close to the soil.
        span(5, 6, 9, leaf);
        span(6, 5, 10, leaf);
        span(7, 4, 11, leaf);
        span(8, 5, 10, leaf);
        span(9, 6, 9, leafDark);
        put(4, 7, leafDark); put(11, 7, leafDark);
        span(9, 5, 6, leafDark); span(9, 9, 10, leafDark);
        put(6, 5, leafLit); put(5, 6, leafLit); put(7, 4, leafLit);
        put(7, 6, leafLit);
        put(6, 10, leafDark); put(9, 10, leafDark);
      } else {
        // Taller, with two pairs of leaves held out flat and a bud on top.
        put(7, 2, leafLit); put(7, 3, leaf);
        span(4, 6, 8, leaf);
        // Lower pair, wider.
        span(7, 3, 6, leaf);
        span(8, 3, 5, leafDark);
        span(7, 9, 12, leaf);
        span(8, 10, 12, leafDark);
        // Upper pair, shorter.
        span(5, 4, 6, leaf);
        span(5, 9, 11, leaf);
        put(4, 5, leafLit); put(11, 5, leafLit);
        put(3, 7, leafLit); put(12, 7, leafLit);
      }
      break;
    }
    case "grassSide": {
      // Turf over the edge. A band of it across the top, then single blades
      // hanging down into the soil - which is what makes it look rooted
      // rather than laid on like a carpet.
      const turf = spec.line ?? [84, 124, 50];
      const turfLit: [number, number, number] = [
        Math.min(255, Math.round(turf[0] * 1.35)),
        Math.min(255, Math.round(turf[1] * 1.25)),
        Math.min(255, Math.round(turf[2] * 1.4)),
      ];
      const turfDark: [number, number, number] = [
        Math.round(turf[0] * 0.72), Math.round(turf[1] * 0.72), Math.round(turf[2] * 0.7),
      ];

      for (let x = 0; x < TILE_PX; x++) {
        // The solid band. Two rows, so it survives being seen from across a
        // field; one row is a line, and a line reads as an edge rather than
        // as grass.
        draw(x, 0, turfLit[0], turfLit[1], turfLit[2]);
        draw(x, 1, turf[0], turf[1], turf[2]);

        // A blade here and there, one row deep and rarely two, so the band
        // breaks up along its bottom edge instead of ending in a straight
        // line. The noise is per-column and seeded off the tile, so the four
        // sides of one block do not all carry the same silhouette.
        //
        // Two rows plus these is an eighth of the block. The ask was five per
        // cent, which on a sixteen-pixel tile is four fifths of one pixel -
        // less than the single top pixel it was meant to replace - so this is
        // the thinnest band that still reads as turf rather than as a line.
        const n = noise(x, 0, tile + 5);
        const blade = n > 0.78 ? 2 : n > 0.42 ? 1 : 0;
        for (let y = 2; y < 2 + blade; y++) {
          const c = y === 1 + blade ? turfDark : turf;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      break;
    }
    case "meat": {
      // A cut on the bone: сало or a steak, with the bone showing at one end.
      // The bone is what makes it read as meat at 16 px rather than as a
      // lump of clay, and it is the one part that does not change colour in
      // the fire - so raw and cooked are the same drawing twice.
      const raw = spec.cooked !== true;
      /*
       * Poultry is pale meat and roasts to gold, not to brown.
       *
       * The same drawing either way - the point of the tile is the cut on the
       * bone - but a курка painted in the pork palette is indistinguishable
       * from the pork in a row of the bag, which is the only thing an
       * inventory icon has to avoid.
       */
      const bird = spec.poultry === true;
      const flesh: [number, number, number] = bird
        ? (raw ? [232, 186, 170] : [206, 156, 84])
        : (raw ? [206, 106, 106] : [150, 88, 54]);
      const fleshLit: [number, number, number] = bird
        ? (raw ? [246, 214, 200] : [230, 186, 114])
        : (raw ? [232, 146, 142] : [186, 122, 76]);
      const fleshDark: [number, number, number] = bird
        ? (raw ? [198, 146, 134] : [158, 110, 52])
        : (raw ? [158, 68, 74] : [104, 58, 36]);
      const fat: [number, number, number] = bird
        ? (raw ? [246, 232, 216] : [232, 204, 156])
        : (raw ? [238, 214, 206] : [212, 180, 150]);
      const bone: [number, number, number] = [232, 226, 208];
      const boneDark: [number, number, number] = [186, 178, 156];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };

      // The cut: a rounded slab across the middle of the tile.
      span(4, 5, 11, flesh);
      span(5, 3, 12, flesh);
      span(6, 2, 13, flesh);
      span(7, 2, 13, flesh);
      span(8, 2, 13, flesh);
      span(9, 3, 13, flesh);
      span(10, 4, 12, flesh);
      span(11, 6, 11, flesh);

      // Light along the top-left, shadow along the bottom-right.
      span(4, 6, 9, fleshLit);
      put(3, 5, fleshLit); put(4, 5, fleshLit);
      put(2, 6, fleshLit); put(2, 7, fleshLit);
      span(10, 6, 11, fleshDark);
      span(11, 7, 10, fleshDark);
      put(13, 8, fleshDark); put(13, 9, fleshDark);

      // A seam of fat, which is what says сало rather than lean steak.
      put(5, 7, fat); put(6, 7, fat); put(7, 8, fat);
      put(9, 6, fat); put(10, 7, fat);

      // The bone, sticking out at the right.
      put(13, 5, bone); put(14, 5, bone);
      put(14, 4, bone); put(15, 4, bone);
      put(15, 5, boneDark); put(14, 6, boneDark);
      break;
    }
    case "karas": {
      // A карась: deep-bodied, blunt-nosed, bronze-olive on the back and pale
      // underneath. Round rather than torpedo-shaped is what tells it from a
      // pike, and it is the only thing distinguishing a fish silhouette at
      // sixteen pixels.
      const cooked = spec.cooked === true;
      const back: [number, number, number] = cooked ? [140, 96, 48] : [122, 122, 62];
      const flank: [number, number, number] = cooked ? [176, 130, 70] : [178, 168, 96];
      const bellyC: [number, number, number] = cooked ? [206, 168, 108] : [222, 214, 168];
      const fin: [number, number, number] = cooked ? [122, 82, 44] : [150, 140, 78];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };

      // The body, nose to the left, deepest through the middle.
      span(5, 6, 10, back);
      span(6, 4, 12, back);
      span(7, 3, 12, flank);
      span(8, 2, 12, flank);
      span(9, 3, 12, flank);
      span(10, 4, 12, bellyC);
      span(11, 6, 10, bellyC);

      // The tail, forked, off the back end.
      put(13, 7, fin); put(13, 8, fin); put(13, 9, fin);
      put(14, 5, fin); put(14, 6, fin); put(14, 10, fin); put(14, 11, fin);
      put(15, 4, fin); put(15, 12, fin);
      put(14, 8, fin);

      // Dorsal and belly fins, and the eye.
      span(4, 7, 10, fin);
      span(12, 7, 9, fin);
      // The eye: white with a pupil in it. Written as two pixels, not three -
      // the first version painted the white and then the pupil over the same
      // cell, so the highlight never existed.
      put(4, 7, [244, 240, 224]);
      put(4, 8, [30, 26, 20]);
      put(3, 8, cooked ? back : [222, 214, 168]);
      break;
    }
    case "rod": {
      // The вудка: a tapering rod up the diagonal with the line hanging off
      // its tip. The line is what makes it a fishing rod rather than a stick,
      // so it gets the full drop of the tile and a float at the bottom.
      const wood: [number, number, number] = [146, 104, 58];
      const woodLit: [number, number, number] = [180, 132, 74];
      const woodDark: [number, number, number] = [100, 70, 38];
      const line: [number, number, number] = [232, 228, 210];
      const floatRed: [number, number, number] = [198, 54, 44];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      // Butt at the bottom-left, tip at the top-right, thinning as it goes.
      for (let k = 0; k < 12; k++) {
        const x = 2 + k;
        const y = 14 - k;
        put(x, y, k < 6 ? wood : woodLit);
        if (k < 7) put(x, y + 1, woodDark);
      }
      // The grip: two darker rows at the butt.
      put(2, 15, woodDark); put(3, 15, woodDark);

      // The line, from the tip and straight down, with a float on it.
      for (let y = 4; y <= 11; y++) put(13, y, line);
      put(13, 12, floatRed);
      put(13, 13, floatRed);
      put(12, 12, floatRed);
      put(14, 12, floatRed);
      break;
    }
    case "needles": {
      // Pine needles: dark blue-green, in short strokes rather than the round
      // blobs of an oak canopy. The strokes are what tell a сосна from an oak
      // at any distance, and the colour alone does not.
      const dark: [number, number, number] = [34, 62, 48];
      const mid: [number, number, number] = [46, 84, 60];
      const lit: [number, number, number] = [66, 112, 76];
      for (let y = 0; y < TILE_PX; y++) {
        for (let x = 0; x < TILE_PX; x++) {
          const n = noise(x, y, tile);
          if (n < 0.30) continue;   // holes: you see sky through a pine
          const c = n > 0.72 ? lit : n > 0.5 ? mid : dark;
          draw(x, y, c[0], c[1], c[2]);
        }
      }
      // Short downward strokes over the top, which is the needle itself.
      for (let x = 0; x < TILE_PX; x += 2) {
        const top = Math.floor(noise(x, 3, tile + 11) * 10);
        for (let y = top; y < Math.min(TILE_PX, top + 3); y++) {
          draw(x, y, lit[0], lit[1], lit[2]);
        }
      }
      break;
    }
    case "resin": {
      // Смола: a bead of amber with the light caught in it. Round, so it does
      // not read as a lump of ore, and warm, so it does not read as glass.
      const amber: [number, number, number] = [196, 122, 30];
      const amberLit: [number, number, number] = [236, 172, 68];
      const amberDark: [number, number, number] = [140, 78, 18];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };
      span(4, 6, 9, amber);
      span(5, 5, 10, amber);
      span(6, 4, 11, amber);
      span(7, 4, 11, amber);
      span(8, 4, 11, amber);
      span(9, 5, 10, amber);
      span(10, 5, 10, amber);
      span(11, 6, 9, amberDark);
      // A drip, because resin runs.
      put(8, 12, amberDark); put(8, 13, amberDark); put(8, 14, amber);
      // Shadow down the right, one bright spot up the left.
      for (const [x, y] of [[11, 6], [11, 7], [11, 8], [10, 9], [10, 10]] as const) {
        put(x, y, amberDark);
      }
      put(6, 6, amberLit); put(5, 6, amberLit); put(6, 5, amberLit);
      break;
    }
    case "torch": {
      // A смолоскип: a stick, a rag bound round its head, and the flame.
      // Cross-rendered, so this one drawing is the whole thing from every
      // side - which is why the flame sits dead centre rather than to one
      // side, and why there is nothing along the edges to look wrong.
      const wood: [number, number, number] = [120, 86, 48];
      const woodDark: [number, number, number] = [82, 58, 32];
      const rag: [number, number, number] = [206, 194, 166];
      const ragDark: [number, number, number] = [158, 144, 118];
      const flame: [number, number, number] = [246, 168, 42];
      const flameHot: [number, number, number] = [254, 232, 150];
      const flameDeep: [number, number, number] = [206, 84, 22];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);

      // The handle, from the bottom of the tile up to two thirds.
      for (let y = 8; y < TILE_PX; y++) {
        put(7, y, wood);
        put(8, y, woodDark);
      }
      // The rag, bound round the head in two bands.
      for (let y = 5; y <= 7; y++) {
        put(6, y, rag); put(7, y, rag); put(8, y, rag); put(9, y, ragDark);
      }
      put(6, 6, ragDark); put(9, 5, ragDark);

      // The flame, tapering upward.
      put(7, 4, flameDeep); put(8, 4, flameDeep);
      put(6, 3, flame); put(7, 3, flameHot); put(8, 3, flameHot); put(9, 3, flame);
      put(7, 2, flameHot); put(8, 2, flame);
      put(7, 1, flame);
      put(7, 0, flameDeep);
      break;
    }
    case "blueprint":
    case "blueprintTop": {
      // A dressed stone with a plan cut into it: the outline of a хата on the
      // sides, and the same building seen from above on the top. Lines rather
      // than a picture - it is a drawing on a stone, and a drawing at this
      // size is three or four strokes or it is a smudge.
      const cut: [number, number, number] = spec.line ?? [72, 92, 116];
      const cutLit: [number, number, number] = [
        Math.min(255, Math.round(cut[0] * 1.5)),
        Math.min(255, Math.round(cut[1] * 1.4)),
        Math.min(255, Math.round(cut[2] * 1.3)),
      ];
      const put = (x: number, y: number, c: [number, number, number]): void =>
        draw(x, y, c[0], c[1], c[2]);
      const span = (
        y: number, x0: number, x1: number, c: [number, number, number],
      ): void => {
        for (let x = x0; x <= x1; x++) put(x, y, c);
      };
      const col = (
        x: number, y0: number, y1: number, c: [number, number, number],
      ): void => {
        for (let y = y0; y <= y1; y++) put(x, y, c);
      };

      if (spec.pattern === "blueprintTop") {
        // The plan: a square with a gap in one wall where the door goes.
        span(3, 3, 12, cut);
        span(12, 3, 12, cut);
        col(3, 3, 12, cut);
        col(12, 3, 12, cut);
        span(3, 7, 8, cutLit);   // the doorway
        break;
      }

      // The elevation: walls, a hipped roof, a door and two windows.
      span(11, 3, 12, cut);            // the ground line
      col(3, 7, 11, cut);              // walls
      col(12, 7, 11, cut);
      span(7, 3, 12, cut);             // the eaves
      // The roof drawing to a ridge.
      span(6, 5, 10, cut);
      span(5, 6, 9, cut);
      span(4, 7, 8, cutLit);
      // A door in the middle and a window each side.
      col(7, 9, 11, cutLit);
      col(8, 9, 11, cutLit);
      put(5, 9, cutLit); put(5, 10, cutLit);
      put(10, 9, cutLit); put(10, 10, cutLit);
      break;
    }
    case "thatch": {
      // Vertical strands with an occasional darker binding row.
      for (let x = 0; x < TILE_PX; x++) {
        if (noise(x, 0, tile + 3) < 0.34) {
          for (let y = 0; y < TILE_PX; y++) set(x, y, 0.45);
        }
      }
      for (let y = 0; y < TILE_PX; y += 8) {
        for (let x = 0; x < TILE_PX; x++) set(x, y, 0.55);
      }
      break;
    }
    default:
      break;
  }
}

/**
 * Paint one 16x16 tile into a standalone RGBA buffer.
 *
 * Both consumers start here, so the inventory icon and the block in the world
 * are literally the same pixels.
 */
/**
 * Drawn with a shaft, which is where such a thing is held.
 *
 * `drawShaft` runs the handle straight from the head to the edge of the
 * tile, and `paintTile` turns the whole drawing over so the head is up
 * and the handle runs down - so the corner nearest the bottom-left, which
 * is where a sabre's hilt is and where the grip rule looks, is not the
 * handle's end but its side. "Гравець тримає за залізо, а не рукоятку."
 * These are the patterns drawn that way; anything else is held the
 * sabre's way.
 */
const SHAFTED: ReadonlySet<string> = new Set(["pickaxe", "shovel", "axe", "hoe", "hoeIron"]);

export function shafted(tile: number): boolean {
  const pattern = TILES[tile]?.pattern;
  return pattern !== undefined && SHAFTED.has(pattern);
}

export function paintTile(tile: number, spec: TileSpec): Uint8Array {
  const out = new Uint8Array(TILE_PX * TILE_PX * 4);
  for (let py = 0; py < TILE_PX; py++) {
    for (let px = 0; px < TILE_PX; px++) {
      // Seed from the tile's position in the sheet so a tile's grain does not
      // change if it is ever moved to a different index.
      const sx = (tile % ATLAS_TILES) * TILE_PX + px;
      const sy = Math.floor(tile / ATLAS_TILES) * TILE_PX + py;

      const n = noise(sx, sy, tile);
      let [r, g, b] = spec.base;
      let alpha = spec.alpha ?? 255;
      if (spec.fleck && noise(sx, sy, tile + 991) < spec.fleck.chance) {
        [r, g, b] = spec.fleck.color;
        alpha = spec.fleck.alpha ?? alpha;
      }
      const k = 1 + (n - 0.5) * 2 * spec.grain;

      const i = (py * TILE_PX + px) * 4;
      out[i] = Math.max(0, Math.min(255, r * k));
      out[i + 1] = Math.max(0, Math.min(255, g * k));
      out[i + 2] = Math.max(0, Math.min(255, b * k));
      // Holes are cut from the same deterministic noise, so a leaf block looks
      // the same every time the world is generated.
      out[i + 3] =
        spec.holes !== undefined && noise(sx, sy, tile + 4441) < spec.holes
          ? 0
          : alpha;
    }
  }
  if (spec.pattern !== undefined) drawPattern(out, tile, spec);
  /*
   * A shafted tool is painted shaft-up, head at the foot - the painters
   * were written that way - and turned over here so it is shown head-up:
   * the way a pick stands in a hand and the way a picture of one reads in
   * a belt. "Картинка в інвентарі догори дриґом."
   */
  if (spec.pattern !== undefined && SHAFTED.has(spec.pattern)) {
    const row = TILE_PX * 4;
    for (let top = 0, bottom = TILE_PX - 1; top < bottom; top++, bottom--) {
      for (let i = 0; i < row; i++) {
        const a = top * row + i, b = bottom * row + i;
        const t = out[a]!; out[a] = out[b]!; out[b] = t;
      }
    }
  }
  return out;
}


/**
 * Pack every tile into one buffer for upload as a texture array.
 *
 * **Rows are flipped.** `paintTile` draws with row 0 at the top, the way any
 * image is drawn. A texture array has flipY off - WebGL ignores it for 3D
 * textures entirely - so data row 0 lands at v = 0, which geometry treats as
 * the *bottom* of a face. Uploading unflipped stands every texture in the
 * world on its head.
 *
 * That went unnoticed for a long time because noise looks the same either way.
 * Wheat does not: it grew downward out of the top of its block.
 */
export function packTextureArray(): Uint8Array {
  const layerBytes = TILE_PX * TILE_PX * 4;
  const out = new Uint8Array(TILE_COUNT * layerBytes);

  for (let tile = 0; tile < TILE_COUNT; tile++) {
    const spec = TILES[tile];
    if (spec === undefined) continue;

    const px = paintTile(tile, spec);
    const base = tile * layerBytes;
    for (let row = 0; row < TILE_PX; row++) {
      const from = row * TILE_PX * 4;
      const to = base + (TILE_PX - 1 - row) * TILE_PX * 4;
      out.set(px.subarray(from, from + TILE_PX * 4), to);
    }
  }
  return out;
}
